import { useEffect, useState, useMemo, useCallback, useRef, FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client"; 
import Layout from "@/components/Layout";
import { Section, SectionLabel, SectionTitle, FadeIn } from "@/components/Section";
import { Fuel, Ship, FileText, TrendingDown, Printer, X, Leaf, Shield, Cloud } from "lucide-react";
import WeatherOperability from "@/components/WeatherOperability";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import operationsBg from "@/assets/operations-bg.jpg";
import velocityLogo from "@/assets/velocity-logo-light.png";
import velocityLogoColor from "@/assets/velocity-logo-color.jpg";
import velocityLogoFullColor from "@/assets/velocity-logo-full-color.png";
import PageHero from "@/components/PageHero";

// ─── NUMBER FORMATTING HELPER ───────────────────────────────
const fmt = (n: number) => (n ?? 0).toLocaleString("en-US");
const fmtM = (n: number) => (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (n: number, d: number = 2) => (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const toHHMM = (hrs: number) => {
  const h = Math.floor(hrs);
  const m = Math.round((hrs - h) * 60);
  return `${h}h${String(m).padStart(2, '0')}`;
};

// VO Branding colors
const VO_RED = "#C53030";
const VO_GREEN = "#1D9E75";

// ─── HELICOPTER DATA ────────────────────────────────────────
// burnGalHr = cruise fuel consumption US gal/hr
// maintDayFlat = typical daily maintenance/support cost (≈$30,000 industry avg — integral to calc, hidden from client)
// hourlyRate = typical industry charter rate $/flight-hr (includes crew, insurance, overhead)
// Cruise speeds are TYPICAL OFFSHORE CRUISE (not Vne/max) per OEM specs and IOGP operating profiles
const HELIS: Record<string, { name: string; config: string; pax: number; kts: number; burnGalHr: number; maintDayFlat: number; hourlyRate: number; standbyFee: number; minDailyHrs: number; notes: string }> = {
  AW139: { name: "Leonardo AW139",    config: "IOGP offshore · standard crew change",   pax: 12, kts: 139, burnGalHr: 100,  maintDayFlat: 28000,  hourlyRate: 3200,  standbyFee: 18000, minDailyHrs: 2, notes: "Medium twin · cruise 139 kts · $2,900–$4,000/hr fuel-exclusive" },
  S92:   { name: "Sikorsky S-92A",    config: "IOGP offshore · standard crew change",   pax: 19, kts: 145, burnGalHr: 170, maintDayFlat: 35000,  hourlyRate: 6500,  standbyFee: 32000, minDailyHrs: 2, notes: "Heavy twin · cruise 145 kts · $5,200–$8,000/hr fuel-exclusive" },
  H175:  { name: "Airbus H175",       config: "IOGP offshore · 16-pax configuration",   pax: 16, kts: 142, burnGalHr: 130, maintDayFlat: 30000,  hourlyRate: 4800,  standbyFee: 22000, minDailyHrs: 2, notes: "Medium-heavy · cruise 142 kts · $4,100–$5,500/hr fuel-exclusive" },
  AS332: { name: "Airbus AS332L2",    config: "Super Puma · IOGP offshore",             pax: 19, kts: 130, burnGalHr: 145, maintDayFlat: 32000,  hourlyRate: 5500,  standbyFee: 28000, minDailyHrs: 2, notes: "Heavy · cruise 130 kts · $4,500–$6,500/hr fuel-exclusive" },
  AW189: { name: "Leonardo AW189",    config: "IOGP offshore · 16-pax configuration",   pax: 16, kts: 144, burnGalHr: 130, maintDayFlat: 30000,  hourlyRate: 4800,  standbyFee: 22000, minDailyHrs: 2, notes: "Super-medium · cruise 144 kts · $4,300–$6,200/hr fuel-exclusive" },
  H160:  { name: "Airbus H160",       config: "IOGP offshore · 12-pax configuration",   pax: 12, kts: 130, burnGalHr: 82,  maintDayFlat: 22000,  hourlyRate: 2900,  standbyFee: 14000, minDailyHrs: 2, notes: "Medium twin · cruise 130 kts · $2,500–$3,700/hr fuel-exclusive" },
  S76D:  { name: "Sikorsky S-76D",    config: "Medium offshore · 12-pax configuration", pax: 12, kts: 145, burnGalHr: 67,  maintDayFlat: 22000,  hourlyRate: 2800,  standbyFee: 14000, minDailyHrs: 2, notes: "Medium · cruise 145 kts · $2,300–$3,200/hr fuel-exclusive" },
};

// ─── COMPETITOR VESSEL DATA ─────────────────────────────────
// Vessel classes by hull design — no operator branding
type CompetitorVessel = {
  name: string; series: string; hullType: string;
  pax: number; kts: number; mgoBurnMTHr: number; maintHr: number; dayRate: number;
  dp: string; loaM: number; notes: string;
};

const COMPETITOR_VESSELS: Record<string, CompetitorVessel> = {
  aircat35_combi: {
    name: "AIRCAT 35 Combi MC", series: "Velocity Phantom Series",
    hullType: "Catamaran", pax: 80, kts: 40, mgoBurnMTHr: 1.39, maintHr: 190, dayRate: 18000,
    dp: "DP2", loaM: 35, notes: "Velocity Phantom Series · 80 pax · 40+ kts · Waterjet propulsion · ~$18,000/day",
  },
  aircat36_combi: {
    name: "AIRCAT 36 Combi MC", series: "Velocity Apex Series",
    hullType: "Catamaran", pax: 60, kts: 40, mgoBurnMTHr: 1.39, maintHr: 210, dayRate: 18000,
    dp: "DP2", loaM: 36, notes: "Velocity Apex Series · 60 pax · 40+ kts · Security/escort capable · ~$18,000/day",
  },
  gulf_craft_fsv: {
    name: "Gulf Craft 202 FSV", series: "McCall-class Monohull FSV",
    hullType: "Monohull", pax: 64, kts: 26, mgoBurnMTHr: 1.39, maintHr: 260, dayRate: 18000,
    dp: "DP2", loaM: 61.6, notes: "202' (61.6m) Gulf Craft FSV · 4× Cummins QSK60 (10,800 HP) · Hamilton HT810 waterjets · 64 pax · ~$16,000–$20,000/day",
  },
  ic0618: {
    name: "Incat Crowther IC0618", series: "50m Catamaran Crewboat",
    hullType: "Catamaran", pax: 149, kts: 35, mgoBurnMTHr: 1.88, maintHr: 280, dayRate: 21500,
    dp: "DP2", loaM: 50.29, notes: "50m Cat · 4× MTU 16V4000 M71 · Hamilton HM811 Waterjets · ~$20,000–$23,000/day",
  },
  ic013009: {
    name: "Incat Crowther IC013009", series: "57m Catamaran Workboat",
    hullType: "Catamaran", pax: 76, kts: 35, mgoBurnMTHr: 1.88, maintHr: 320, dayRate: 23000,
    dp: "DP2", loaM: 57.25, notes: "57m Cat · 4× Cummins QSK95 (16,000 hp) · HamiltonJet HM810 · ~$23,000/day",
  },
  mauric_fsh170: {
    name: "MAURIC / SURF FSH 170", series: "Fully Shaped Hull FSIV",
    hullType: "Monohull", pax: 50, kts: 20, mgoBurnMTHr: 0.60, maintHr: 180, dayRate: 6500,
    dp: "None", loaM: 52, notes: "MAURIC FSH monohull · Waterjets + bow thruster · Oldest, no DP · ~$5,000–$8,000/day",
  },
  midship_marine_175: {
    name: "Midship Marine 175 FSIV", series: "175ft FSIV — Older DP2 Gen",
    hullType: "Monohull", pax: 60, kts: 20, mgoBurnMTHr: 0.65, maintHr: 200, dayRate: 11500,
    dp: "DP2", loaM: 53.37, notes: "Midship Marine 175' design · 4× Waterjets + 2 bow thrusters · ~$10,000–$13,000/day",
  },
  piriou_seas175_conv: {
    name: "PIRIOU / MAURIC SEAS 175", series: "Conventional Bow — Workhorse Series",
    hullType: "Monohull", pax: 60, kts: 20, mgoBurnMTHr: 0.65, maintHr: 200, dayRate: 13500,
    dp: "DP2", loaM: 53.37, notes: "PIRIOU + MAURIC design · Conventional bow · Workhorse series · ~$12,000–$15,000/day",
  },
  piriou_seas175_sb: {
    name: "PIRIOU / MAURIC SEAS 175 SB", series: "Straight Bow — Newest Gen",
    hullType: "Monohull", pax: 60, kts: 30, mgoBurnMTHr: 0.72, maintHr: 220, dayRate: 16000,
    dp: "DP2", loaM: 54, notes: "Straight-bow hull · 5,360 kW · FiFi II + WS2 · Newest gen, best speed · ~$14,000–$18,000/day",
  },
  surfer250: {
    name: "Surfer 250", series: "Surfer Series",
    hullType: "Monohull", pax: 70, kts: 30, mgoBurnMTHr: 0.68, maintHr: 200, dayRate: 14000,
    dp: "DP2", loaM: 25.95, notes: "25.95m crewboat · 70 pax · 30 kts · 4.00 MT cargo · ~$12,000–$16,000/day",
  },
  surfer2600: {
    name: "Surfer 2600", series: "Surfer Series",
    hullType: "Monohull", pax: 50, kts: 30, mgoBurnMTHr: 0.65, maintHr: 200, dayRate: 13000,
    dp: "DP2", loaM: 25.95, notes: "25.95m crewboat · 50 pax · 30 kts · 4.00 MT cargo · ~$11,000–$15,000/day",
  },
  surfer320: {
    name: "Surfer 320", series: "Surfer Series",
    hullType: "Monohull", pax: 90, kts: 37, mgoBurnMTHr: 0.85, maintHr: 230, dayRate: 17000,
    dp: "DP2", loaM: 33.50, notes: "33.50m crewboat · 55–90 pax · 37 kts · 5.00 MT cargo · ~$15,000–$19,000/day",
  },
  surfer320_combi: {
    name: "Surfer 320 Combi", series: "Surfer Series — Fi-Fi",
    hullType: "Monohull", pax: 60, kts: 37, mgoBurnMTHr: 0.88, maintHr: 240, dayRate: 18000,
    dp: "DP2", loaM: 34.33, notes: "34.33m combi crewboat · 46–60 pax · 37 kts · 5.00 MT cargo · Fi-Fi capable · ~$16,000–$20,000/day",
  },
  surfer3600: {
    name: "Surfer 3600", series: "Surfer Series — High Speed",
    hullType: "Monohull", pax: 50, kts: 30, mgoBurnMTHr: 1.07, maintHr: 250, dayRate: 20000,
    dp: "DP2", loaM: 36.30, notes: "36.30m high-speed crewboat · 50 pax · 30 kts · 2.00 MT cargo · ~$18,000–$22,000/day",
  },
};

const VO_BLUE = "#2563EB";
const VO_AMBER = "#D97706";

// ─── AIRCAT 35 CONSTANTS ────────────────────────────────────
// Per AIRCAT Technical Assessment (ExxonMobil Worldwide, Ref. 202601/JA/0872)
const AIRCAT_SPEED_KTS = 39; // metocean-weighted annual avg operating speed
const AIRCAT_PAX = 80;       // 80 industrial personnel capacity
const AIRCAT_MGO_BURN_MT_HR = 1.39; // 1625 L/hr × 0.855 kg/L ÷ 1000
const AIRCAT_MAINT_HR = 190;
const AIRCAT_MAX_SPEED = 53;
const AIRCAT_AVAIL_PCT = 98.12;
// ─── BLOCK TYPE ─────────────────────────────────────────────
type Block = {
  name: string; region: string; nm: number; base: string; fpso: string; operator: string;
  effKts: number; avail: number; meanHs: number; fieldHrs: number; seaDesc: string;
  status?: "producing" | "development" | "pre-fid";
  defaultHeliKey?: string; defaultHeliHourlyRate?: number; defaultAircatDayRate?: number;
  pob?: string; heliOperator?: string; heliSeatRate?: number;
  // Field-centric metadata
  fieldName?: string; blocks?: string; waterDepth?: string; bpInterest?: string;
  partners?: string; regulatory?: string; licence?: string; coords?: string;
  discoveryYear?: string; productionStart?: string; resourcesInPlace?: string;
  productionUnit?: string;
};

// ─── OPERATING AREA BLOCKS ─────────────────────────────────
const BLOCKS: Block[] = [
  // ── Global Operating Areas ──
  { name: "Stabroek Block, Guyana", fieldName: "Stabroek Block (Liza Ph.1 & Ph.2, Payara, Yellowtail)", region: "Caribbean / Latin America", nm: 120, base: "Georgetown / Vreed-en-Hoop", fpso: "Liza Destiny FPSO · Liza Unity FPSO · Prosperity FPSO · ONE GUYANA FPSO", operator: "XOM Operator (45%) / Hess (30%) / CNOOC (25%)", effKts: 39, avail: 96, meanHs: 1.25, fieldHrs: 1.5, seaDesc: "Benign to moderate; NE trade wind swell, low Hs, minimal weather downtime; ~1,750m WD", status: "producing", waterDepth: "~1,750m", defaultHeliKey: "S92" },
  { name: "Block 58, Suriname",            region: "Caribbean / Latin America", nm: 120, base: "Paramaribo, Suriname",     fpso: "Sapakara / Krabdagu FPSOs (TotalEnergies)",        operator: "TotalEnergies / APA Corporation",      effKts: 39, avail: 97.8, meanHs: 1.60, fieldHrs: 1.5, seaDesc: "Hs 1.25–2.0m · NE trade-wind swell" },
  { name: "French Guiana (Emerging)",      region: "Caribbean / Latin America", nm: 140, base: "Cayenne, French Guiana",   fpso: "TBD — offshore prospect (Montagne d'Or region)", operator: "TBD — legislative framework pending",  effKts: 39, avail: 97.5, meanHs: 1.60, fieldHrs: 1.5, seaDesc: "Hs 1.25–2.0m · NE trade-wind swell" },
  { name: "Pre-Salt Campos Basin, Brazil", region: "South America",             nm: 130, base: "Macaé, Brazil",            fpso: "FPSO Cidade de Itaguaí · P-77 · Sepetiba",       operator: "Petrobras / Equinor",                  effKts: 39, avail: 95.8, meanHs: 1.75, fieldHrs: 1.5, seaDesc: "Hs 1.5–2.25m · SE Atlantic swell · Tp 8–12s" },
  { name: "Pre-Salt Santos Basin, Brazil", region: "South America",             nm: 200, base: "Rio de Janeiro, Brazil",   fpso: "FPSO Carioca · FPSO Buzios (multiple units)",     operator: "Petrobras / Shell / TotalEnergies",    effKts: 39, avail: 94.5, meanHs: 2.00, fieldHrs: 1.5, seaDesc: "Hs 1.75–2.5m · SW Atlantic long-period swell" },
  { name: "Deepwater GoM (MC/GC), USA",    region: "Gulf of Mexico",            nm: 160, base: "Port Fourchon, Louisiana", fpso: "Argos (Mad Dog 2) · Whale · Anchor",              operator: "BP / Shell / Chevron",                 effKts: 39, avail: 93.5, meanHs: 1.25, fieldHrs: 1.5, seaDesc: "Hs 0.75–1.75m (ops days) · hurricane season Jun–Oct" },
  { name: "Norwegian North Sea",           region: "Europe",                    nm: 150, base: "Bergen, Norway",           fpso: "Johan Castberg · Balder X · Valhall",             operator: "Equinor / Aker BP / ConocoPhillips",   effKts: 39, avail: 80.5, meanHs: 2.50, fieldHrs: 1.5, seaDesc: "Hs 2.0–4.0m dominant · N Atlantic swell · ~80% ops window" },
  { name: "UK Central North Sea",          region: "Europe",                    nm: 180, base: "Aberdeen, Scotland",       fpso: "Cambo · Rosebank · Jackdaw",                      operator: "Equinor / Shell / Harbour Energy",     effKts: 39, avail: 81.2, meanHs: 2.50, fieldHrs: 1.5, seaDesc: "Hs 2.0–3.75m · N Atlantic swell" },
  { name: "Angola Block 32 (Pre-Salt)",    region: "West Africa",               nm: 200, base: "Luanda, Angola",           fpso: "CLOV · Kaombo Norte/Sul · Palas",                 operator: "TotalEnergies / BP / Equinor",         effKts: 39, avail: 93.5, meanHs: 1.75, fieldHrs: 1.5, seaDesc: "Hs 1.5–2.0m · SW Atlantic swell" },
  { name: "Nigeria Deepwater (OML 130)",   region: "West Africa",               nm: 130, base: "Lagos, Nigeria",           fpso: "FPSO Akpo · Egina · Agbami",                      operator: "TotalEnergies / Shell / Chevron",      effKts: 39, avail: 97.0, meanHs: 1.25, fieldHrs: 1.5, seaDesc: "Hs 1.0–1.5m · Gulf of Guinea equatorial swell" },
  { name: "Mozambique Rovuma",             region: "East Africa",               nm: 80,  base: "Pemba, Mozambique",        fpso: "Coral Sul FLNG · Area 1 FLNG (planned)",          operator: "ENI / TotalEnergies / ExxonMobil",     effKts: 39, avail: 95.0, meanHs: 1.50, fieldHrs: 1.0, seaDesc: "Hs 1.25–1.75m · SE trade-wind swell" },
  { name: "UAE / Qatar — Persian Gulf",    region: "Middle East",               nm: 80,  base: "Abu Dhabi / Doha",         fpso: "Nasr CPP · Umm Lulu · Al Shaheen",               operator: "ADNOC / QatarEnergy / ExxonMobil",     effKts: 39, avail: 99.0, meanHs: 0.50, fieldHrs: 1.0, seaDesc: "Hs 0.25–0.75m dominant · sheltered enclosed sea" },
  { name: "Malaysia Deepwater (Sabah)",    region: "Asia Pacific",              nm: 120, base: "Labuan, Malaysia",         fpso: "FPSO Kikeh · Gumusut-Kakap · Malikai",            operator: "Murphy / Shell / Petronas",            effKts: 39, avail: 97.2, meanHs: 1.00, fieldHrs: 1.0, seaDesc: "Hs 0.75–1.5m · Sulu Sea / South China Sea" },
  { name: "NW Shelf, Australia",           region: "Asia Pacific",              nm: 200, base: "Karratha, Australia",      fpso: "FPSO Pyrenees Venture · Ichthys CPF",             operator: "BHP / Chevron / INPEX",                effKts: 39, avail: 85.0, meanHs: 2.50, fieldHrs: 1.5, seaDesc: "Hs 2.0–3.0m · Indian Ocean swell · cyclone season Dec–Apr" },

  // ── Additional Deepwater Fields ──
  {
    name: "Mad Dog Field, GoM", fieldName: "Mad Dog", blocks: "Green Canyon Block 780 / 825 / 870",
    status: "producing",
    region: "Gulf of Mexico", nm: 113, base: "Port Fourchon, Louisiana",
    fpso: "Argos FPU (semi-sub, 60,000t) + Mad Dog Spar (Phase 1)", operator: "BP (60.5%) / Woodside (23.9%) / Chevron (15.6%)",
    effKts: 39, avail: 93.5, meanHs: 1.25, fieldHrs: 1.5, seaDesc: "Hs 0.75–1.75m · hurricane season Jun–Oct",
    defaultHeliKey: "S92", defaultHeliHourlyRate: 5000, defaultAircatDayRate: 22000,
    pob: "~250", heliOperator: "PHI Aviation · 10-yr BP contract · Houma, LA", heliSeatRate: 2200,
    waterDepth: "1,370–2,200 m (4,500–7,200 ft)", bpInterest: "60.5%", partners: "Woodside Energy 23.9%, Chevron/UNOCAL 15.6%",
    regulatory: "BOEM — US Outer Continental Shelf", licence: "OCS-G 9723 (primary)",
    coords: "27°58'N 90°31'W", discoveryYear: "1998", productionStart: "2005 (Phase 1) / April 2023 (Phase 2 Argos)",
    resourcesInPlace: ">5 billion BOE (field-wide)", productionUnit: "Argos FPU (semi-sub) + Mad Dog Spar",
  },
  {
    name: "Kaskida Field, GoM", fieldName: "Kaskida", blocks: "Keathley Canyon Block 292 (+ KC 248, 291, 293, 336–338, 379–381)",
    status: "development",
    region: "Gulf of Mexico", nm: 190, base: "Port Fourchon, Louisiana",
    fpso: "New semi-sub FPU (4-column OPTI hull, EXMAR)", operator: "BP (100%)",
    effKts: 39, avail: 93.5, meanHs: 1.25, fieldHrs: 1.5, seaDesc: "Hs 0.75–1.75m · hurricane season Jun–Oct · ultra-deepwater",
    defaultHeliKey: "S92", defaultHeliHourlyRate: 5500, defaultAircatDayRate: 24000,
    pob: "TBD (minimum manning)", heliOperator: "TBD — S-92A or AW189 required (190nm range)", heliSeatRate: 2400,
    waterDepth: "~1,829 m (6,000 ft)", bpInterest: "100%", partners: "None",
    regulatory: "BOEM — US Outer Continental Shelf", licence: "KC Block 292 (acquired 2003)",
    coords: "26°28'N 93°07'W", discoveryYear: "2006", productionStart: "Expected 2029",
    resourcesInPlace: "~10 billion BOE (Kaskida + Tiber catchment)", productionUnit: "Semi-sub FPU (OPTI hull, EXMAR / Audubon / Seatrium)",
  },
  {
    name: "Tiber–Guadalupe Field, GoM", fieldName: "Tiber (+ Guadalupe tieback)", blocks: "Keathley Canyon Block 102 (Tiber) / KC Block 59 (Guadalupe)",
    status: "pre-fid",
    region: "Gulf of Mexico", nm: 200, base: "Port Fourchon, Louisiana",
    fpso: "New semi-sub FPU (mirror design to Kaskida)", operator: "BP (operator, interest TBC)",
    effKts: 39, avail: 93.5, meanHs: 1.25, fieldHrs: 1.5, seaDesc: "Hs 0.75–1.75m · hurricane season Jun–Oct · sub-salt Wilcox",
    defaultHeliKey: "S92", defaultHeliHourlyRate: 5500, defaultAircatDayRate: 24000,
    pob: "TBD (minimum manning)", heliOperator: "TBD — S-92A or AW189 required (200nm range)", heliSeatRate: 2400,
    waterDepth: "Deepwater — Paleogene sub-salt", bpInterest: "Operator (% TBC)", partners: "Petrobras (historical JV on some leases)",
    regulatory: "BOEM — US Outer Continental Shelf", licence: "KC Block 102 and adjacent leases",
    coords: "~26°45'N 93°18'W", discoveryYear: "2009", productionStart: "Expected 2026–2027 (FID anticipated 2025)",
    resourcesInPlace: "Part of 10 billion BOE Kaskida-Tiber catchment", productionUnit: "Semi-sub FPU (6 Tiber + 2 Guadalupe tieback wells)",
  },
  {
    name: "Atlantis Field, GoM", fieldName: "Atlantis", blocks: "Green Canyon Blocks 699 / 743 / 744 (+ GC 700, 745)",
    status: "producing",
    region: "Gulf of Mexico", nm: 130, base: "Port Fourchon, Louisiana",
    fpso: "Atlantis semi-sub platform (Phase 3 expansion ongoing)", operator: "BP (56%) / Woodside Energy (44%)",
    effKts: 39, avail: 93.5, meanHs: 1.25, fieldHrs: 1.5, seaDesc: "Hs 0.75–1.75m · hurricane season Jun–Oct",
    defaultHeliKey: "S92", defaultHeliHourlyRate: 5000, defaultAircatDayRate: 22000,
    pob: "~200", heliOperator: "PHI Aviation S-92A (BP GoM contract)", heliSeatRate: 2200,
    waterDepth: "~2,150 m (7,050 ft)", bpInterest: "56%", partners: "BHP / Woodside Energy 44%",
    regulatory: "BOEM — US Outer Continental Shelf", licence: "OCS-G 22747 and related",
    coords: "27°12'N 90°01'W", discoveryYear: "1998", productionStart: "2007",
    resourcesInPlace: "~2 billion BOE", productionUnit: "Atlantis semi-sub platform",
  },
  {
    name: "Thunder Horse Field, GoM", fieldName: "Thunder Horse", blocks: "Mississippi Canyon Block 778 (+ MC 734, 776, 777, 779, 822)",
    status: "producing",
    region: "Gulf of Mexico", nm: 125, base: "Port Fourchon, Louisiana",
    fpso: "Thunder Horse PDQ (Production, Drilling & Quarters) semi-sub", operator: "BP (75%) / ExxonMobil (25%)",
    effKts: 39, avail: 93.5, meanHs: 1.25, fieldHrs: 1.5, seaDesc: "Hs 0.75–1.75m · hurricane season Jun–Oct",
    defaultHeliKey: "S92", defaultHeliHourlyRate: 5000, defaultAircatDayRate: 22000,
    pob: "~240", heliOperator: "PHI Aviation / Bristow (BP GoM contract)", heliSeatRate: 2100,
    waterDepth: "~1,920 m (6,300 ft)", bpInterest: "75%", partners: "ExxonMobil 25%",
    regulatory: "BOEM — US Outer Continental Shelf", licence: "OCS-G 15416 (primary)",
    coords: "28°11'N 88°33'W", discoveryYear: "1999", productionStart: "2008",
    resourcesInPlace: "~1 billion BOE (+ South Expansion ~25K bpd)", productionUnit: "Thunder Horse PDQ semi-sub",
  },
  {
    name: "Na Kika Field, GoM", fieldName: "Na Kika", blocks: "Mississippi Canyon Block 474 (+ MC 429, 430, 473, 475, 518, 519)",
    status: "producing",
    region: "Gulf of Mexico", nm: 145, base: "Port Fourchon, Louisiana",
    fpso: "Na Kika semi-sub host platform (subsea tieback hub)", operator: "BP (50%) / Shell (50%)",
    effKts: 39, avail: 93.5, meanHs: 1.25, fieldHrs: 1.5, seaDesc: "Hs 0.75–1.75m · hurricane season Jun–Oct",
    defaultHeliKey: "S92", defaultHeliHourlyRate: 5000, defaultAircatDayRate: 22000,
    pob: "~150", heliOperator: "PHI Aviation / Bristow (BP GoM contract)", heliSeatRate: 2200,
    waterDepth: "~2,350 m (7,700 ft)", bpInterest: "50%", partners: "Shell 50%",
    regulatory: "BOEM — US Outer Continental Shelf", licence: "OCS-G 18882 (primary)",
    coords: "27°57'N 89°21'W", discoveryYear: "1997", productionStart: "2003",
    resourcesInPlace: "Multiple subsea fields (tieback hub)", productionUnit: "Na Kika semi-sub host platform",
  },

  {
    name: "Schiehallion / Loyal Field, WoS", fieldName: "Schiehallion & Loyal (Quad 204)", blocks: "UK Block 204/20a (Schiehallion) / 204/24a (Loyal)",
    status: "producing",
    region: "North Sea (UKCS)", nm: 95, base: "Sumburgh Airport, Shetland, UK",
    fpso: "Glen Lyon FPSO (270m × 52m, 130,000 bpd, 800,000 bbl storage)", operator: "BP (36.3%) / Shell (54.03%) / OMV (9.67%)",
    effKts: 39, avail: 81.2, meanHs: 2.50, fieldHrs: 1.5, seaDesc: "Hs 2.0–3.75m · N Atlantic swell · West of Shetland",
    defaultHeliKey: "S92", defaultHeliHourlyRate: 5000, defaultAircatDayRate: 20000,
    pob: "Up to 140 (125 single cabins)", heliOperator: "Bristow Group (Sumburgh) / OHS (Aberdeen)", heliSeatRate: 1800,
    waterDepth: "~500 m (1,640 ft)", bpInterest: "36.3%", partners: "Shell 54.03%, OMV 9.67%",
    regulatory: "NSTA — UK Continental Shelf", licence: "UK Production Licence P.233 (Quad 204)",
    coords: "60°15'N 004°45'W", discoveryYear: "1993 (Schiehallion) / 1994 (Loyal)", productionStart: "1998 (original) / May 2017 (Glen Lyon)",
    resourcesInPlace: "~450 million bbls remaining (Quad 204 target)", productionUnit: "Glen Lyon FPSO — world's largest harsh-water FPSO",
  },

  {
    name: "Greater Tortue Ahmeyim (GTA), West Africa", fieldName: "Greater Tortue Ahmeyim (GTA)", blocks: "Block C-8 (Mauritania) / Saint-Louis Profond (Senegal)",
    status: "producing",
    region: "West Africa", nm: 22, base: "Dakar, Senegal (logistics · 205nm) · 22nm from coastline",
    fpso: "FPSO Tortue (gas processing) → FLNG Gimi (nearshore LNG)", operator: "BP (~56–60%) / Kosmos Energy (~27%) / Petrosen (~10%) / SMH (~10%)",
    effKts: 39, avail: 97.0, meanHs: 1.25, fieldHrs: 1.0, seaDesc: "Hs 1.0–1.5m · West Africa coast · benign metocean",
    defaultHeliKey: "AW139", defaultHeliHourlyRate: 2500, defaultAircatDayRate: 18000,
    pob: "Up to 140", heliOperator: "Operator TBC · AW139 viable at 22nm", heliSeatRate: 1400,
    waterDepth: "120 m (FPSO) / up to 2,850 m (subsea wellheads)", bpInterest: "~56–60%", partners: "Kosmos Energy ~27%, Petrosen ~10%, SMH/SMHPM ~10%",
    regulatory: "Mauritania EPSC (Block C-8) / Senegal EPSC (Saint-Louis Profond)", licence: "Block C-8 EPSC (2012) / Saint-Louis Profond EPSC (2012)",
    coords: "20°54'N 017°20'W", discoveryYear: "2015", productionStart: "2024 (Phase 1 first gas)",
    resourcesInPlace: "~15 Tcf recoverable gas", productionUnit: "FPSO Tortue + FLNG Gimi (2.3–2.7 mtpa LNG)",
  },

  // ── ExxonMobil Worldwide Operating Blocks ──
  {
    name: "Hammerhead, Guyana", fieldName: "Hammerhead (7th Stabroek development — FID 2025)",
    status: "development",
    region: "Caribbean / Latin America", nm: 130, base: "Georgetown / Vreed-en-Hoop",
    fpso: "New FPSO (TBD) — Target: ~150,000 bpd online 2029", operator: "XOM Operator (45%)",
    effKts: 39, avail: 96, meanHs: 1.25, fieldHrs: 1.5, seaDesc: "Benign to moderate; NE trade wind swell; similar metocean to Stabroek cluster",
    defaultHeliKey: "S92", waterDepth: "~1,750m",
  },
  {
    name: "OML 133 — Erha / Erha North", fieldName: "OML 133 — Erha / Erha North",
    status: "producing",
    region: "West Africa", nm: 52, base: "Onne / Port Harcourt",
    fpso: "Erha FPSO (2.2 MMbbl storage; 210,000 bpd cap.)", operator: "XOM Operator (56.25%) / Shell SNEPCO (43.75%)",
    effKts: 39, avail: 94, meanHs: 1.50, fieldHrs: 1.5, seaDesc: "Moderate; equatorial Atlantic, Gulf of Guinea; long-period low-frequency swell; seasonal harmattan; 1,000–1,200m WD",
    defaultHeliKey: "AW139", waterDepth: "1,000–1,200m",
  },
  {
    name: "OML 138 — Usan", fieldName: "OML 138 — Usan",
    status: "producing",
    region: "West Africa", nm: 54, base: "Onne / Port Harcourt",
    fpso: "Usan FPSO", operator: "XOM Operator / TotalEnergies / Chevron / CNOOC",
    effKts: 39, avail: 94, meanHs: 1.50, fieldHrs: 1.5, seaDesc: "Moderate; equatorial Atlantic, Gulf of Guinea; ~34 subsea wells; $1.5B redevelopment 2025–27; 750–850m WD",
    defaultHeliKey: "AW139", waterDepth: "750–850m",
  },
  {
    name: "OML 139 / OPL 223 — Owowo", fieldName: "OML 139 / OPL 223 — Owowo",
    status: "development",
    region: "West Africa", nm: 130, base: "Onne / Port Harcourt",
    fpso: "Planned tie-back to Usan FPSO (~180,000 bpd peak at FID)", operator: "XOM Operator (27%) / Chevron (27%) / TotalEnergies (18%) / Nexen (18%) / NNPC (10%)",
    effKts: 39, avail: 93, meanHs: 1.60, fieldHrs: 1.5, seaDesc: "Moderate; deepwater Gulf of Guinea; development phase; planned Usan FPSO tie-back; ~500M–1B bbl recoverable",
    defaultHeliKey: "S92", waterDepth: "Deepwater",
  },
  {
    name: "Block 15 — Kizomba A/B, Mondo, Saxi-Batuque", fieldName: "Block 15 — Kizomba A/B, Mondo, Saxi-Batuque",
    status: "producing",
    region: "West Africa", nm: 78, base: "Luanda / Soyo",
    fpso: "Kizomba A FPSO · Kizomba B FPSO · Mondo FPSO · Saxi-Batuque FPSO", operator: "XOM Operator (36%) / Azule Energy (42%) / Equinor (12%) / Sonangol (10%)",
    effKts: 39, avail: 95, meanHs: 1.40, fieldHrs: 1.5, seaDesc: "Moderate; SE swell, South Atlantic; generally benign, occasional SE trade wind strengthening; ~1,200m WD; PSC extended to 2037",
    defaultHeliKey: "AW139", waterDepth: "~1,200m",
  },
  {
    name: "Block 17 — Girassol / Dalia / Pazflor / CLOV", fieldName: "Block 17 — Girassol / Dalia / Pazflor / CLOV",
    status: "producing",
    region: "West Africa", nm: 85, base: "Luanda",
    fpso: "Girassol FPSO · Dalia FPSO · Pazflor FPSO · CLOV FPSO", operator: "TotalEnergies Operator (38%) / XOM (19%) / Equinor (22.16%) / Azule Energy (15.84%) / Sonangol (5%)",
    effKts: 39, avail: 95, meanHs: 1.40, fieldHrs: 1.5, seaDesc: "Moderate; SE swell, South Atlantic; 4,000 km² block; ~5B boe gross recoverable; 15 discoveries to date",
    defaultHeliKey: "AW139", waterDepth: "Deepwater",
  },
  {
    name: "Block 32 — Kaombo", fieldName: "Block 32 — Kaombo",
    status: "producing",
    region: "West Africa", nm: 120, base: "Luanda",
    fpso: "Kaombo Norte FPSO · Kaombo Sul FPSO", operator: "TotalEnergies Operator (30%) / XOM (15%) / other partners",
    effKts: 39, avail: 94, meanHs: 1.60, fieldHrs: 1.5, seaDesc: "Moderate to rough; South Atlantic SE swell; further offshore and deeper; >1B boe recoverable; 13 discoveries",
    defaultHeliKey: "S92", waterDepth: "Deepwater",
  },
  {
    name: "BM-S-8 — Bacalhau (Carcara)", fieldName: "BM-S-8 — Bacalhau (Carcara)",
    status: "producing",
    region: "South America", nm: 100, base: "Santos / Macaé",
    fpso: "FPSO Carioca MV31 (First oil Q4 2025)", operator: "XOM Operator (40%) / Equinor (40%) / Repsol Sinopec (20%)",
    effKts: 39, avail: 93, meanHs: 2.15, fieldHrs: 1.5, seaDesc: "Moderate to rough; SW swell dominates Santos Basin; higher Hs than West Africa; pre-salt ~2,000m WD; first oil Q4 2025",
    defaultHeliKey: "S92", waterDepth: "~2,000m",
  },
  {
    name: "North Field East — Qatar", fieldName: "North Field East — Qatar",
    status: "producing",
    region: "Middle East", nm: 80, base: "Ras Laffan",
    fpso: "LNG trains (12 of 14 trains) · Condensate refinery — no FPSO", operator: "Qatargas JV / XOM (25%) / QatarEnergy (75%)",
    effKts: 39, avail: 97, meanHs: 1.00, fieldHrs: 1.0, seaDesc: "Benign; Arabian/Persian Gulf; flat-calm periods dominant; LNG megaproject — not conventional FPSO operations",
    defaultHeliKey: "AW139", waterDepth: "Shallow",
  },

  // ── Azule Energy (bp / Eni JV) — Angola, Lower Congo Basin ──
  {
    name: "Block 18 — Greater Plutonio (Azule)", fieldName: "Greater Plutonio (Galio, Cromio, Paladio, Plutonio, Cobalto)", blocks: "Block 18",
    status: "producing",
    region: "West Africa", nm: 87, base: "Luanda (Sonils Base) · 99nm from Soyo (Kwanda) · 50nm from shoreline",
    fpso: "FPSO Greater Plutonio (240,000 bopd · 1.77M bbl storage)", operator: "Azule Energy (operated)",
    effKts: 39, avail: 95, meanHs: 1.60, fieldHrs: 1.5, seaDesc: "Moderate; South Atlantic SE swell, Hs 1.25–2.0m; benign majority of year",
    defaultHeliKey: "AW139", waterDepth: "1,200–1,500m", partners: "bp / Eni joint venture (Azule Energy)",
    coords: "7°46'S 012°11'E (VERIFY — derived)", productionStart: "2007",
    productionUnit: "FPSO Greater Plutonio — Platinum infill drilling ongoing",
  },
  {
    name: "Block 31 — PSVM (Azule)", fieldName: "PSVM (Plutao, Saturno, Venus, Marte)", blocks: "Block 31",
    status: "producing",
    region: "West Africa", nm: 101, base: "Soyo (Kwanda Base) · 217nm from Luanda · 101nm from shoreline",
    fpso: "FPSO PSVM (157,000 bopd · 1.6M bbl storage)", operator: "Azule Energy (operated)",
    effKts: 39, avail: 94, meanHs: 1.70, fieldHrs: 1.5, seaDesc: "Moderate; deepest of the Azule fleet and longest transit; South Atlantic SE swell",
    defaultHeliKey: "S92", waterDepth: "~2,000m", partners: "bp / Eni joint venture (Azule Energy)",
    coords: "6°15'S 010°39'E (VERIFY — derived)", productionStart: "2012",
    productionUnit: "FPSO PSVM",
  },
  {
    name: "Block 15/06 — West Hub (Azule)", fieldName: "West Hub (Sangos, Cinguvu, Mpungi, Vandumbu, Ndungu EP)", blocks: "Block 15/06",
    status: "producing",
    region: "West Africa", nm: 78, base: "Soyo (Kwanda Base) · 207nm from Luanda · 77nm from shoreline",
    fpso: "FPSO N'Goma (100,000 bopd)", operator: "Azule Energy (operated)",
    effKts: 39, avail: 95, meanHs: 1.55, fieldHrs: 1.5, seaDesc: "Moderate; South Atlantic SE swell; benign majority of year",
    defaultHeliKey: "AW139", waterDepth: "1,000–1,500m", partners: "bp / Eni joint venture (Azule Energy)",
    coords: "6°08'S 011°02'E (AIS sourced)", productionStart: "2014 (ex Xikomba)",
    productionUnit: "FPSO N'Goma — life extension work from 2026",
  },
  {
    name: "Block 15/06 — East Hub (Azule)", fieldName: "East Hub (Cabaca South East, Cabaca North, UM8)", blocks: "Block 15/06",
    status: "producing",
    region: "West Africa", nm: 56, base: "Soyo (Kwanda Base) · 193nm from Luanda · 56nm from shoreline",
    fpso: "FPSO Armada Olombendo (80,000 bopd · 1.7M bbl storage)", operator: "Azule Energy (operated)",
    effKts: 39, avail: 96, meanHs: 1.50, fieldHrs: 1.5, seaDesc: "Moderate; shallower shelf-edge water depths; South Atlantic SE swell",
    defaultHeliKey: "AW139", waterDepth: "450–550m", partners: "bp / Eni joint venture (Azule Energy)",
    coords: "6°09'S 011°24'E (VERIFY)", productionStart: "February 2017",
    productionUnit: "FPSO Armada Olombendo",
  },
  {
    name: "Block 15/06 — Agogo Integrated West Hub (Azule)", fieldName: "Agogo Integrated West Hub (Agogo, Ndungu full field)", blocks: "Block 15/06",
    status: "producing",
    region: "West Africa", nm: 89, base: "Soyo (Kwanda Base) · 210nm from Luanda · 88nm from shoreline",
    fpso: "FPSO Agogo (120,000 bopd · 1.6M bbl storage · post-combustion carbon capture)", operator: "Azule Energy (operated)",
    effKts: 39, avail: 95, meanHs: 1.60, fieldHrs: 1.5, seaDesc: "Moderate; South Atlantic SE swell; ultra-deepwater West Hub area",
    defaultHeliKey: "S92", waterDepth: "~1,700m", partners: "bp / Eni joint venture (Azule Energy) · Yinson 15-yr firm charter from Aug 2025",
    coords: "6°12'S 010°52'E (VERIFY)", productionStart: "First oil 29 July 2025",
    productionUnit: "FPSO Agogo — first FPSO with post-combustion carbon capture",
  },
  {
    name: "NGC — Quiluma & Maboqueiro (Azule)", fieldName: "New Gas Consortium — Quiluma and Maboqueiro", blocks: "Shallow water Lower Congo (NGC)",
    status: "development",
    region: "West Africa", nm: 34, base: "Soyo (Kwanda Base) · 155nm from Luanda · 32nm from shoreline",
    fpso: "Fixed shallow-water gas platforms (no FPSO) — 300+ MMscfd to ALNG", operator: "Azule Energy (operated, NGC JV)",
    effKts: 39, avail: 97, meanHs: 1.35, fieldHrs: 1.0, seaDesc: "Benign; shallow water off Soyo; short transit, high CTV suitability",
    defaultHeliKey: "AW139", waterDepth: "<100m", partners: "NGC joint venture (Azule Energy operated)",
    coords: "6°33'S 011°57'E (VERIFY)", productionStart: "First gas early 2026",
    productionUnit: "Quiluma and Maboqueiro fixed platforms — crew demand ex Soyo",
  },
  {
    name: "Blocks 31 & 31/21 — Greater PAJ (Azule)", fieldName: "Greater PAJ — Palas, Astraea, Juno (Blk 31), Urano, Dione (Blk 31/21)", blocks: "Blocks 31 and 31/21",
    status: "development",
    region: "West Africa", nm: 96, base: "Soyo (Kwanda Base) · 208nm from Luanda · 95nm from shoreline",
    fpso: "FPSO Greater PAJ (planned · 95,000 bopd · 70 MMscfd gas export to ALNG)", operator: "Azule Energy (operated)",
    effKts: 39, avail: 94, meanHs: 1.70, fieldHrs: 1.5, seaDesc: "Moderate; ultra-deepwater Block 31 SE area; South Atlantic SE swell",
    defaultHeliKey: "S92", waterDepth: "~2,000m", partners: "bp / Eni joint venture (Azule Energy)",
    coords: "6°21'S 010°45'E (VERIFY — planned)", productionStart: "FID 22 June 2026 · first oil expected H1 2029",
    productionUnit: "New-build FPSO (pipeline entry)",
  },
];

// ─── HISTORICAL DATA ────────────────────────────────────────
const H_LABELS = ["Jan 24","Feb 24","Mar 24","Apr 24","May 24","Jun 24","Jul 24","Aug 24","Sep 24","Oct 24","Nov 24","Dec 24","Jan 25","Feb 25","Mar 25","Apr 25","May 25","Jun 25","Jul 25","Aug 25","Sep 25","Oct 25","Nov 25","Dec 25","Jan 26","Feb 26","Mar 26"];
const H_JET    = [840,820,810,840,870,890,880,865,845,820,800,790,795,805,815,810,820,835,850,860,845,835,825,820,825,830,835];
const H_MGO    = [625,615,605,628,652,668,660,645,630,610,595,585,590,595,602,598,606,618,628,635,622,614,606,601,604,608,612];
const P_LABELS = ["Apr 26","May 26","Jun 26","Jul 26","Aug 26","Sep 26","Oct 26","Nov 26","Dec 26"];
const PD_JET   = [5,15,30,40,35,25,15,5,0];
const PD_MGO   = [5,15,25,35,30,20,10,5,0];

// Conversion helpers: $/MT ↔ $/US gal
// gallons_per_MT = (1000 kg / density_kg_per_L) / 3.78541 L/gal
const JETA1_KG_PER_L = 0.804;
const MGO_KG_PER_L   = 0.845;
const L_PER_GAL      = 3.78541;
const GAL_PER_MT_JET = 1000 / JETA1_KG_PER_L / L_PER_GAL; // ~328.6
const GAL_PER_MT_MGO = 1000 / MGO_KG_PER_L   / L_PER_GAL; // ~312.5
const jetPerGal  = (priceMT: number) => +(priceMT / GAL_PER_MT_JET).toFixed(2);
const mgoPerGal  = (priceMT: number) => +(priceMT / GAL_PER_MT_MGO).toFixed(2);

type FuelUnit = "metric" | "imperial";

const FALLBACK = { jet: 855, mgo: 650 };

const buildPriceChartData = (prices: typeof FALLBACK) => {
  const data: { month: string; jet?: number; mgo?: number; jetProj?: number; mgoProj?: number }[] = [];
  H_LABELS.forEach((label, i) => {
    data.push({ month: label, jet: H_JET[i], mgo: H_MGO[i] });
  });
  const lastIdx = H_LABELS.length - 1;
  data[lastIdx].jetProj = prices.jet;
  data[lastIdx].mgoProj = prices.mgo;
  P_LABELS.forEach((label, i) => {
    data.push({
      month: label,
      jetProj: prices.jet + PD_JET[i],
      mgoProj: prices.mgo + PD_MGO[i],
    });
  });
  return data;
};

const buildSpreadData = () => H_LABELS.map((label, i) => ({
  month: label,
  spread: H_JET[i] - H_MGO[i],
  jetPrice: H_JET[i],
  mgoPrice: H_MGO[i],
  pctPremium: Math.round(((H_JET[i] - H_MGO[i]) / H_MGO[i]) * 100),
}));

const buildIndexData = (p: typeof FALLBACK) => [
  { fuel: "Jet A-1 (Helicopter)", index: 100, fill: VO_RED },
  { fuel: "MGO (AIRCAT)", index: Math.round(p.mgo / p.jet * 100), fill: VO_GREEN },
];

// ─── CALCULATION MODEL ──────────────────────────────────────
// Helicopter: standby fee + max(actual flight hrs, min guaranteed hrs) × hourly rate + fuel + maintenance
// AIRCAT:     day rate + (trips × RT hrs × 0.52 MT/hr × $/MT) + maintenance
function calcAll(
  heliKey: string,
  distanceNm: number,
  totalPax: number,
  heliHourlyRate: number,
  aircatDayRate: number,
  jetFuelPerGal: number,
  mgoPriceMT: number,
  opsDays: number,
  heliStandbyFee?: number,
  heliMinDailyHrs?: number,
  heliSpeedKts?: number,
  aircatSpeedKts?: number,
) {
  const heli = HELIS[heliKey];
  const standby = heliStandbyFee ?? heli.standbyFee;
  const minHrs = heliMinDailyHrs ?? heli.minDailyHrs;

  // ── Helicopter ──
  const effectiveHeliSpeed = heliSpeedKts ?? heli.kts;
  const heliFlights = Math.ceil(totalPax / heli.pax);
  const heliRTHrs = (distanceNm / effectiveHeliSpeed) * 2;
  const heliTotalFlightHrs = heliRTHrs * heliFlights;
  const heliBillableHrs = Math.max(heliTotalFlightHrs, minHrs);
  const heliCharterCost = heliBillableHrs * heliHourlyRate;
  const heliStandbyCost = standby;
  const heliTotalFuelGal = heliTotalFlightHrs * heli.burnGalHr;
  const heliTotalFuelCost = heliTotalFuelGal * jetFuelPerGal;
  const heliMaintCost = heli.maintDayFlat;
  const heliDailyTotal = heliStandbyCost + heliCharterCost + heliTotalFuelCost + heliMaintCost;
  const heliCostPerPax = heliDailyTotal / totalPax;

  // ── AIRCAT 35 ──
  const effectiveAircatSpeed = aircatSpeedKts ?? AIRCAT_SPEED_KTS;
  const aircatTrips = Math.ceil(totalPax / AIRCAT_PAX);
  const aircatRTHrs = (distanceNm / effectiveAircatSpeed) * 2;
  const aircatFuelMTPerTrip = aircatRTHrs * AIRCAT_MGO_BURN_MT_HR;
  const aircatTotalFuelCost = aircatFuelMTPerTrip * mgoPriceMT * aircatTrips;
  const aircatMaintCost = AIRCAT_MAINT_HR * aircatRTHrs * aircatTrips;
  const aircatDailyTotal = aircatDayRate + aircatTotalFuelCost + aircatMaintCost;
  const aircatCostPerPax = aircatDailyTotal / totalPax;

  // ── Annual ──
  const heliAnnual = heliDailyTotal * opsDays;
  const aircatAnnual = aircatDailyTotal * opsDays;

  // ── Savings ──
  const dailySaving = heliDailyTotal - aircatDailyTotal;
  const annualSaving = heliAnnual - aircatAnnual;
  const pctAdvantage = totalPax > 0 && heliCostPerPax > 0
    ? Math.round((1 - aircatCostPerPax / heliCostPerPax) * 100)
    : 0;

  // ── CO₂ (indicative) ──
  const heliCO2 = heliTotalFuelGal * 9.6;
  const aircatCO2 = aircatFuelMTPerTrip * aircatTrips * 3170;
  const heliCO2PerPax = heliCO2 / totalPax;
  const aircatCO2PerPax = aircatCO2 / totalPax;

  return {
    heliFlights, heliRTHrs, heliTotalFlightHrs, heliBillableHrs, heliCharterCost, heliStandbyCost, heliTotalFuelGal, heliTotalFuelCost, heliMaintCost, heliDailyTotal, heliCostPerPax,
    aircatTrips, aircatRTHrs, aircatFuelMTPerTrip, aircatTotalFuelCost, aircatMaintCost, aircatDailyTotal, aircatCostPerPax,
    heliAnnual, aircatAnnual, dailySaving, annualSaving, pctAdvantage,
    heliCO2PerPax, aircatCO2PerPax,
  };
}

// ─── COMPETITOR VESSEL CALCULATION ──────────────────────────
function calcCompetitorVessel(
  vesselKey: string,
  distanceNm: number,
  totalPax: number,
  mgoPriceMT: number,
  customDayRate?: number,
  customSpeedKts?: number,
) {
  const v = COMPETITOR_VESSELS[vesselKey];
  const effectiveDayRate = customDayRate ?? v.dayRate;
  const effectiveSpeed = customSpeedKts ?? v.kts;
  const trips = Math.ceil(totalPax / v.pax);
  const rtHrs = (distanceNm / effectiveSpeed) * 2;
  const fuelMTPerTrip = rtHrs * v.mgoBurnMTHr;
  const totalFuelCost = fuelMTPerTrip * mgoPriceMT * trips;
  const maintCost = v.maintHr * rtHrs * trips;
  const dailyTotal = effectiveDayRate + totalFuelCost + maintCost;
  const costPerPax = dailyTotal / totalPax;
  const co2 = fuelMTPerTrip * trips * 3170;
  const co2PerPax = co2 / totalPax;
  return { trips, rtHrs, fuelMTPerTrip, totalFuelCost, maintCost, dailyTotal, costPerPax, co2PerPax };
}

type CompetitorResult = ReturnType<typeof calcCompetitorVessel>;

type CalcResult = ReturnType<typeof calcAll>;

type FieldResult = {
  block: Block;
  blockIdx: number;
  r: CalcResult;
  pax: number;
  distance: number;
  heliHourlyRate: number;
  aircatDayRate: number;
  heliKey: string;
};

type AggregateResult = {
  totalHeliDaily: number;
  totalAircatDaily: number;
  totalHeliAnnual: number;
  totalAircatAnnual: number;
  totalDailySaving: number;
  totalAnnualSaving: number;
  totalPax: number;
  avgHeliCostPerPax: number;
  avgAircatCostPerPax: number;
  avgPctAdvantage: number;
  fieldResults: FieldResult[];
};

function calcAggregate(
  fieldResults: FieldResult[],
  opsDays: number,
): AggregateResult {
  const totalHeliDaily = fieldResults.reduce((s, f) => s + f.r.heliDailyTotal, 0);
  const totalAircatDaily = fieldResults.reduce((s, f) => s + f.r.aircatDailyTotal, 0);
  const totalHeliAnnual = totalHeliDaily * opsDays;
  const totalAircatAnnual = totalAircatDaily * opsDays;
  const totalDailySaving = totalHeliDaily - totalAircatDaily;
  const totalAnnualSaving = totalHeliAnnual - totalAircatAnnual;
  const totalPax = fieldResults.reduce((s, f) => s + f.pax, 0);
  const avgHeliCostPerPax = totalPax > 0 ? totalHeliDaily / totalPax : 0;
  const avgAircatCostPerPax = totalPax > 0 ? totalAircatDaily / totalPax : 0;
  const avgPctAdvantage = avgHeliCostPerPax > 0
    ? Math.round((1 - avgAircatCostPerPax / avgHeliCostPerPax) * 100)
    : 0;
  return {
    totalHeliDaily, totalAircatDaily, totalHeliAnnual, totalAircatAnnual,
    totalDailySaving, totalAnnualSaving, totalPax,
    avgHeliCostPerPax, avgAircatCostPerPax, avgPctAdvantage, fieldResults,
  };
}

// ─── METRIC CARD ────────────────────────────────────────────
const MetricCard = ({ label, value, unit, color, change, src }: { label: string; value: number; unit: string; color: string; change: number; src: string }) => {
  const pct = ((change / (value - change)) * 100).toFixed(1);
  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold font-mono" style={{ color }}>{fmtD(value)}</span>
        <span className="text-sm text-muted-foreground">/{unit}</span>
      </div>
      <div className={`text-xs font-mono mt-1 ${change > 0 ? "text-destructive" : "text-teal"}`}>
        {change > 0 ? "+" : ""}{fmtD(change)} {change > 0 ? "▲" : "▼"} {Math.abs(parseFloat(pct))}% vs baseline
      </div>
      <div className="text-[10px] text-muted-foreground/60 mt-1">{src}</div>
    </div>
  );
};

// ─── SPEC ROW ───────────────────────────────────────────────
const SpecRow = ({ label, value, variant = "neutral", bold = false }: { label: string; value: string; variant?: "good" | "warn" | "neutral"; bold?: boolean }) => (
  <div className={`flex justify-between py-1.5 border-b border-border/50 last:border-none text-sm ${bold ? "border-t-2 border-t-border mt-1 pt-2" : ""}`}>
    <span className={`text-muted-foreground ${bold ? "font-bold" : ""}`}>{label}</span>
    <span className={`font-mono font-medium text-right max-w-[55%] ${bold ? "font-bold" : ""} ${variant === "good" ? "text-teal" : variant === "warn" ? "text-red-600" : "text-foreground"}`} style={variant === "warn" ? { color: "#C53030" } : undefined}>{value}</span>
  </div>
);

// ─── INPUT FIELD ────────────────────────────────────────────
const InputField = ({ label, value, onChange, prefix, suffix, min, max, step, type = "number", className: extraClass }: {
  label?: string; value: number; onChange: (v: number) => void;
  prefix?: string; suffix?: string; min?: number; max?: number; step?: number; type?: string; className?: string;
}) => {
  const [draft, setDraft] = useState<string>(String(value));
  const prev = useRef(value);
  // Sync from parent only when value changes externally (not from our own typing)
  if (value !== prev.current) { prev.current = value; if (String(value) !== draft) setDraft(String(value)); }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDraft(raw);
    // Only propagate valid numbers — no clamping during typing
    const n = parseFloat(raw);
    if (!isNaN(n)) { prev.current = n; onChange(n); }
  };

  const handleBlur = () => {
    const n = parseFloat(draft);
    if (isNaN(n) || draft === "") {
      const fallback = min ?? 0;
      setDraft(String(fallback));
      prev.current = fallback;
      onChange(fallback);
    } else {
      // Clamp on blur only
      const clamped = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, n));
      if (clamped !== n) {
        setDraft(String(clamped));
        prev.current = clamped;
        onChange(clamped);
      }
    }
  };

  const inner = (
    <div className="flex items-center gap-1">
      {prefix && <span className="text-sm text-muted-foreground font-mono">{prefix}</span>}
      <input
        type="number"
        value={draft}
        onChange={handleChange}
        onBlur={handleBlur}
        min={min} max={max} step={step}
        className={extraClass ?? "w-full px-3 py-2 border border-border rounded-lg text-sm bg-card text-foreground font-mono focus:border-secondary focus:outline-none"}
      />
      {suffix && <span className="text-sm text-muted-foreground font-mono whitespace-nowrap">{suffix}</span>}
    </div>
  );

  if (!label) return inner;
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{label}</label>
      {inner}
    </div>
  );
};

// ─── REPORT MODAL ───────────────────────────────────────────
type ReportModalProps = {
  open: boolean; onClose: () => void;
  heliKey: string; agg: AggregateResult;
  jetFuelPerGal: number; mgoPriceMT: number;
  // Extended data for full comparison
  allHeliResults?: { key: string; heli: typeof HELIS[string]; avgCostPerPax: number; totalDaily: number; totalAnnual: number; fieldCalcs: CalcResult[] }[];
  heliRateOverrides?: Record<string, number>;
  selectedVesselResults?: { key: string; vessel: CompetitorVessel; avgCostPerPax: number; totalDaily: number; totalAnnual: number; fieldCalcs: CompetitorResult[] }[];
  vesselDayRateOverrides?: Record<string, number>;
  opsDays?: number;
  fieldBlocks?: { name: string; fieldName?: string; nm: number }[];
  fieldPaxList?: number[];
};

const ReportModal = ({ open, onClose, heliKey, agg, jetFuelPerGal, mgoPriceMT, allHeliResults = [], heliRateOverrides = {}, selectedVesselResults = [], vesselDayRateOverrides = {}, opsDays = 330, fieldBlocks = [], fieldPaxList = [] }: ReportModalProps) => {
  const [scope, setScope] = useState<"combined" | "heli" | "vessel">("combined");
  const showHeli = scope !== "vessel";
  const showVessel = scope !== "heli";
  const scopeLabel = scope === "heli"
    ? "Helicopter vs Vessel Comparison"
    : scope === "vessel"
      ? "Vessel vs Vessel Comparison"
      : "Combined Helicopter & Vessel Comparison";

  useEffect(() => {
    if (!open) {
      document.body.classList.remove("report-print-open");
      return;
    }
    document.body.classList.add("report-print-open");
    return () => document.body.classList.remove("report-print-open");
  }, [open]);

  if (!open) return null;
  const heli = HELIS[heliKey];
  const aircatWins = agg.avgAircatCostPerPax < agg.avgHeliCostPerPax;
  const savPct = Math.abs(agg.avgPctAdvantage);
  const date = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const multiField = agg.fieldResults.length > 1;
  const primaryBlock = agg.fieldResults[0]?.block;

  // ── Vessel-to-vessel head-to-head (AIRCAT 35 vs each selected CTV) ──
  const vesselComparisons = selectedVesselResults
    .map(svr => {
      const dailyDelta = svr.totalDaily - agg.totalAircatDaily;
      const annualDelta = svr.totalAnnual - agg.totalAircatAnnual;
      const paxDelta = svr.avgCostPerPax - agg.avgAircatCostPerPax;
      const pct = svr.avgCostPerPax > 0 ? Math.round((1 - agg.avgAircatCostPerPax / svr.avgCostPerPax) * 100) : 0;
      return { ...svr, dailyDelta, annualDelta, paxDelta, pct };
    })
    .sort((a, b) => a.avgCostPerPax - b.avgCostPerPax);
  const bestAltVessel = vesselComparisons[0];
  const bestHeliAlt = [...allHeliResults].sort((a, b) => a.avgCostPerPax - b.avgCostPerPax)[0];

  // ── Savings headline numbers ──
  const heliAnnualSaving = agg.totalHeliAnnual - agg.totalAircatAnnual;
  const heliDailySaving = agg.totalHeliDaily - agg.totalAircatDaily;
  const vesselAnnualSaving = bestAltVessel ? bestAltVessel.annualDelta : 0;
  const vesselDailySaving = bestAltVessel ? bestAltVessel.dailyDelta : 0;
  const combinedFleetSaving = heliAnnualSaving + Math.max(0, vesselAnnualSaving);
  const headlineSaving = scope === "heli" ? heliAnnualSaving : scope === "vessel" ? vesselAnnualSaving : combinedFleetSaving;
  const co2SavedPerPax = agg.fieldResults.reduce((s, f) => s + (f.r.heliCO2PerPax - f.r.aircatCO2PerPax), 0) / (agg.fieldResults.length || 1);



  return (
    <div id="report-print-overlay" className="fixed inset-0 z-50 bg-foreground/50 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div id="report-print-container" className="max-w-[860px] mx-auto my-8 bg-white rounded-xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="rpt-document">
          {/* Report Header */}
          <div className="rpt-header" style={{ background: "#1D2F5C", color: "#fff", padding: "28px 32px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <img src={velocityLogoFullColor} alt="Velocity Offshore" style={{ height: 48, width: "auto" }} />
              <div style={{ fontSize: 13, opacity: 0.7, borderLeft: "1px solid rgba(255,255,255,0.3)", paddingLeft: 16 }}>Offshore Marine Transportation & Support Services</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12, borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 16 }}>
              <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Report type</strong>{multiField ? "Multi-Field Portfolio Analysis" : "Crew Transfer Cost Analysis"}</div>
              <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Report type</strong>{scopeLabel}{multiField ? " · Multi-Field Portfolio" : ""}</div>
              <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Prepared by</strong>Velocity Offshore Holdings LLC</div>
              <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>{multiField ? "Fields analysed" : "Operating area"}</strong>{multiField ? `${agg.fieldResults.length} fields` : primaryBlock?.name}</div>
              <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Date</strong>{date}</div>
              {showHeli && <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Helicopter baseline</strong>{heli.name}</div>}
              <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>CTV</strong>AIRCAT 35 Crewliner (Velocity Offshore)</div>
            </div>
          </div>

          {/* ── EXECUTIVE SAVINGS SUMMARY (page 1) ── */}
          <RptSection title={`Executive Summary — Savings at a Glance (${scopeLabel})`}>
            <div style={{ background: "linear-gradient(135deg,#0F6E56,#1D9E75)", borderRadius: 10, padding: "20px 24px", color: "#fff", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" as any }}>
              <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.85, marginBottom: 6 }}>Total identified annual saving</div>
              <div style={{ fontSize: 38, fontWeight: 800, fontFamily: "monospace", lineHeight: 1.1 }}>
                ${fmtM(Math.max(0, headlineSaving) / 1_000_000)}M / year
              </div>
              <div style={{ fontSize: 12, marginTop: 8, opacity: 0.95 }}>
                Based on {agg.fieldResults.length} field{agg.fieldResults.length > 1 ? "s" : ""}, {agg.totalPax} pax/day and {opsDays} operating days per year, at current Jet A-1 (${fmtD(jetFuelPerGal)}/gal) and MGO (${fmt(mgoPriceMT)}/MT) prices.
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: scope === "combined" ? "1fr 1fr" : "1fr", gap: 12, marginTop: 14 }}>
              {showHeli && (
              <div style={{ border: "1px solid #d3d1c7", borderRadius: 8, padding: "14px 16px" }}>
                <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280", marginBottom: 4 }}>Vessel vs Helicopter</div>
                <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "monospace", color: heliAnnualSaving > 0 ? "#0F6E56" : "#C53030" }}>
                  ${fmtM(Math.abs(heliAnnualSaving) / 1_000_000)}M/yr
                </div>
                <div style={{ fontSize: 11, color: "#4b5563", marginTop: 4 }}>
                  AIRCAT 35 at ${fmtD(agg.avgAircatCostPerPax)}/pax vs {heli.name} at ${fmtD(agg.avgHeliCostPerPax)}/pax
                  {" "}({savPct}% {aircatWins ? "lower" : "higher"}) · ${fmt(Math.round(Math.abs(heliDailySaving)))}/day
                </div>
              </div>
              )}
              {showVessel && (
              <div style={{ border: "1px solid #d3d1c7", borderRadius: 8, padding: "14px 16px" }}>
                <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280", marginBottom: 4 }}>Vessel vs Vessel</div>
                {bestAltVessel ? (
                  <>
                    <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "monospace", color: vesselAnnualSaving > 0 ? "#0F6E56" : "#C53030" }}>
                      ${fmtM(Math.abs(vesselAnnualSaving) / 1_000_000)}M/yr
                    </div>
                    <div style={{ fontSize: 11, color: "#4b5563", marginTop: 4 }}>
                      AIRCAT 35 at ${fmtD(agg.avgAircatCostPerPax)}/pax vs best benchmarked CTV {bestAltVessel.vessel.name} at ${fmtD(bestAltVessel.avgCostPerPax)}/pax
                      {" "}({Math.abs(bestAltVessel.pct)}% {bestAltVessel.pct > 0 ? "lower" : "higher"}) · ${fmt(Math.round(Math.abs(vesselDailySaving)))}/day
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: "#4b5563" }}>No competitor CTV selected — add benchmark vessels in the comparator to include a vessel-to-vessel analysis.</div>
                )}
              </div>
              )}
            </div>

            <table className="rpt-tbl" style={{ marginTop: 14 }}>
              <thead>
                <tr>
                  <th>Comparison</th>
                  <th>Benchmark</th>
                  <th>Benchmark $/pax</th>
                  <th style={{ color: VO_GREEN }}>AIRCAT 35 $/pax</th>
                  <th>Saving/day</th>
                  <th>Saving/year</th>
                </tr>
              </thead>
              <tbody>
                {showHeli && (
                <tr>
                  <td style={{ fontWeight: 600 }}>Vessel vs Helicopter</td>
                  <td>{heli.name}</td>
                  <td className="rpt-warn">${fmtD(agg.avgHeliCostPerPax)}</td>
                  <td className="rpt-good">${fmtD(agg.avgAircatCostPerPax)}</td>
                  <td className={heliDailySaving > 0 ? "rpt-good" : "rpt-warn"}>${fmt(Math.round(Math.abs(heliDailySaving)))}</td>
                  <td className={heliAnnualSaving > 0 ? "rpt-good" : "rpt-warn"}>${fmtM(Math.abs(heliAnnualSaving) / 1_000_000)}M</td>
                </tr>
                )}
                {showHeli && bestHeliAlt && bestHeliAlt.heli.name !== heli.name && (
                  <tr>
                    <td style={{ fontWeight: 600 }}>Vessel vs cheapest helicopter</td>
                    <td>{bestHeliAlt.heli.name}</td>
                    <td className="rpt-warn">${fmtD(bestHeliAlt.avgCostPerPax)}</td>
                    <td className="rpt-good">${fmtD(agg.avgAircatCostPerPax)}</td>
                    <td className={bestHeliAlt.totalDaily - agg.totalAircatDaily > 0 ? "rpt-good" : "rpt-warn"}>${fmt(Math.round(Math.abs(bestHeliAlt.totalDaily - agg.totalAircatDaily)))}</td>
                    <td className={bestHeliAlt.totalAnnual - agg.totalAircatAnnual > 0 ? "rpt-good" : "rpt-warn"}>${fmtM(Math.abs(bestHeliAlt.totalAnnual - agg.totalAircatAnnual) / 1_000_000)}M</td>
                  </tr>
                )}
                {(showVessel ? vesselComparisons : []).map(vc => (
                  <tr key={vc.key}>
                    <td style={{ fontWeight: 600 }}>Vessel vs vessel</td>
                    <td>{vc.vessel.name}</td>
                    <td className={vc.pct > 0 ? "rpt-warn" : "rpt-good"}>${fmtD(vc.avgCostPerPax)}</td>
                    <td className={vc.pct > 0 ? "rpt-good" : "rpt-warn"}>${fmtD(agg.avgAircatCostPerPax)}</td>
                    <td className={vc.dailyDelta > 0 ? "rpt-good" : "rpt-warn"}>${fmt(Math.round(Math.abs(vc.dailyDelta)))}</td>
                    <td className={vc.annualDelta > 0 ? "rpt-good" : "rpt-warn"}>${fmtM(Math.abs(vc.annualDelta) / 1_000_000)}M</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="rpt-p" style={{ marginTop: 10 }}>
              <strong>Why this matters:</strong> the AIRCAT 35 moves {AIRCAT_PAX} personnel per voyage at {AIRCAT_SPEED_KTS} knots on MGO, replacing multiple rotary-wing sorties and out-performing conventional crewboats on cost per passenger.
              {showHeli && co2SavedPerPax > 0 && <> It also removes approximately {fmt(Math.round(co2SavedPerPax))} kg of CO₂ per passenger transferred versus the helicopter baseline.</>}
              {" "}Full field-by-field {scope === "heli" ? "vessel-to-helicopter" : scope === "vessel" ? "vessel-to-vessel" : "vessel-to-helicopter and vessel-to-vessel"} analyses supporting these numbers follow.
            </p>
          </RptSection>

          {/* About */}

          <RptSection title="About Velocity Offshore Holdings LLC">
            <p className="rpt-p"><strong>Mission:</strong> To provide best-in-class offshore vessel operations that enable our clients to execute upstream energy projects safely, efficiently, and sustainably, while delivering superior returns to stakeholders through disciplined asset management and operational excellence.</p>
            <p className="rpt-p"><strong>Vision:</strong> To become the preferred offshore vessel operator in high-growth frontier and emerging markets, recognised for operational reliability, fleet quality, and a forward-looking approach to decarbonization and digital operations.</p>
            <p className="rpt-p">Velocity Offshore Holdings LLC is a Marshall Islands-incorporated offshore vessel holding company established to acquire, manage, and operate offshore support vessels (OSVs) across strategically selected markets in the Americas and beyond.</p>
            <p className="rpt-p">Velocity's crew logistics concept positions the high-capacity AIRCAT 35 CTV as the primary mode for routine crew transfer — delivering significantly lower cost per passenger transferred, relieving helideck scheduling pressure, and providing weather resilience.</p>
          </RptSection>

          {/* Multi-field portfolio summary */}
          {showHeli && multiField && (
            <RptSection title="Portfolio Summary — All Selected Fields">
              <div style={{ background: aircatWins ? "linear-gradient(135deg,#E1F5EE,#d0f0e0)" : "linear-gradient(135deg,#FEF3C7,#FDE68A)", borderRadius: 8, padding: "14px 18px", margin: "12px 0" }}>
                <div style={{ fontSize: 12, color: aircatWins ? "#0F6E56" : "#92400E", marginBottom: 4, fontWeight: 600 }}>
                  {aircatWins
                    ? `AIRCAT 35 delivers ${savPct}% lower average cost per passenger across ${agg.fieldResults.length} fields`
                    : `${heli.name} is ${savPct}% cheaper on average across ${agg.fieldResults.length} fields`
                  }
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: aircatWins ? "#085041" : "#78350F", fontFamily: "monospace" }}>
                  {aircatWins
                    ? `Saving $${fmtM(Math.abs(agg.totalAnnualSaving) / 1_000_000)}M/year over the lowest performing asset`
                    : `Portfolio cost: $${fmtM(agg.totalAircatAnnual / 1_000_000)}M/yr vs $${fmtM(agg.totalHeliAnnual / 1_000_000)}M/yr`
                  }
                </div>
                <div style={{ fontSize: 12, color: aircatWins ? "#0F6E56" : "#92400E", marginTop: 8 }}>
                  Combined daily: ${fmt(Math.round(agg.totalAircatDaily))} (AIRCAT) vs ${fmt(Math.round(agg.totalHeliDaily))} (Heli) · {agg.totalPax} total pax/day
                </div>
              </div>

              <table className="rpt-tbl" style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Distance</th>
                    <th>Pax</th>
                    <th style={{ color: VO_RED }}>Heli $/pax</th>
                    <th style={{ color: VO_GREEN }}>AIRCAT $/pax</th>
                    <th>Saving/day</th>
                    <th>Saving/yr</th>
                  </tr>
                </thead>
                <tbody>
                  {agg.fieldResults.map((fr, i) => {
                    const w = fr.r.aircatCostPerPax < fr.r.heliCostPerPax;
                    return (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{fr.block.fieldName || fr.block.name}</td>
                        <td>{fr.distance} nm</td>
                        <td>{fr.pax}</td>
                        <td className={w ? "rpt-warn" : "rpt-good"}>${fmtD(fr.r.heliCostPerPax)}</td>
                        <td className={w ? "rpt-good" : "rpt-warn"}>${fmtD(fr.r.aircatCostPerPax)}</td>
                        <td className={fr.r.dailySaving > 0 ? "rpt-good" : "rpt-warn"}>${fmt(Math.round(Math.abs(fr.r.dailySaving)))}</td>
                        <td className={fr.r.annualSaving > 0 ? "rpt-good" : "rpt-warn"}>${fmtM(Math.abs(fr.r.annualSaving) / 1_000_000)}M</td>
                      </tr>
                    );
                  })}
                  <tr style={{ fontWeight: 700, borderTop: "2px solid #1D2F5C" }}>
                    <td>PORTFOLIO TOTAL</td>
                    <td>—</td>
                    <td>{agg.totalPax}</td>
                    <td className={aircatWins ? "rpt-warn" : "rpt-good"}>${fmtD(agg.avgHeliCostPerPax)} avg</td>
                    <td className={aircatWins ? "rpt-good" : "rpt-warn"}>${fmtD(agg.avgAircatCostPerPax)} avg</td>
                    <td className={agg.totalDailySaving > 0 ? "rpt-good" : "rpt-warn"}>${fmt(Math.round(Math.abs(agg.totalDailySaving)))}</td>
                    <td className={agg.totalAnnualSaving > 0 ? "rpt-good" : "rpt-warn"}>${fmtM(Math.abs(agg.totalAnnualSaving) / 1_000_000)}M</td>
                  </tr>
                </tbody>
              </table>
            </RptSection>
          )}

          {/* Per-field detail sections */}
          {(showHeli ? agg.fieldResults : []).map((fr, fi) => {
            const r = fr.r;
            const block = fr.block;
            const fWins = r.aircatCostPerPax < r.heliCostPerPax;
            const fSavPct = Math.abs(r.pctAdvantage);
            return (
              <div key={fi}>
                <RptSection title={`${multiField ? `Field ${fi + 1}: ` : ""}${block.fieldName || block.name} — Scenario Configuration`}>
                  <RptTable headers={["Parameter", "Value"]} rows={[
                    ["Operating area / block", block.name],
                    ["Shore base", block.base],
                    ["FPSO examples", block.fpso],
                    ["Operator(s)", block.operator],
                    ["Distance to FPSO", `${fr.distance} nm`],
                    ["Daily crew pax to transfer", `${fr.pax} pax`],
                    ["Helicopter baseline", `${heli.name} — ${heli.pax} pax · ${heli.kts} kts · ${heli.burnGalHr} gal/hr`],
                    ["Helicopter hourly rate", `$${fmt(fr.heliHourlyRate)}/flight-hr`],
                    ["Jet fuel price", `$${fmtD(jetFuelPerGal)}/US gal`],
                    ["AIRCAT 35 cruise speed", `${AIRCAT_SPEED_KTS} knots`],
                    ["AIRCAT 35 capacity", `${AIRCAT_PAX} pax`],
                    ["AIRCAT 35 MGO burn rate", `${AIRCAT_MGO_BURN_MT_HR} MT/hr`],
                    ["AIRCAT 35 day rate", `$${fmt(fr.aircatDayRate)}/day`],
                    ["MGO price", `$${fmt(mgoPriceMT)}/MT`],
                  ]} />
                </RptSection>

                <RptSection title={`${multiField ? `Field ${fi + 1}: ` : ""}Vessel Comparison — Helicopter vs AIRCAT 35`}>
                  <table className="rpt-tbl">
                    <thead><tr><th>Parameter</th><th style={{ color: VO_RED }}>{heli.name}</th><th style={{ color: VO_GREEN }}>AIRCAT 35 ✓</th></tr></thead>
                    <tbody>
                      <tr><td>Passenger capacity</td><td className="rpt-warn">{heli.pax} pax</td><td className="rpt-good">{AIRCAT_PAX} pax</td></tr>
                      <tr><td>Round trips required ({fr.pax} pax)</td><td className="rpt-warn">{r.heliFlights} flights</td><td className="rpt-good">{r.aircatTrips} voyage{r.aircatTrips > 1 ? "s" : ""}</td></tr>
                      <tr><td>Round trip time ({fr.distance} nm)</td><td>{toHHMM(r.heliRTHrs)} per flight × {r.heliFlights} = {toHHMM(r.heliRTHrs * r.heliFlights)}</td><td>{toHHMM(r.aircatRTHrs)} per trip{r.aircatTrips > 1 ? ` × ${r.aircatTrips}` : ""}</td></tr>
                      <tr><td>Fuel type</td><td className="rpt-warn">Jet A-1 (${fmtD(jetFuelPerGal)}/gal)</td><td className="rpt-good">MGO (${fmt(mgoPriceMT)}/MT)</td></tr>
                      <tr><td>Fuel burn rate</td><td>{heli.burnGalHr} gal/hr</td><td>{AIRCAT_MGO_BURN_MT_HR} MT/hr</td></tr>
                      <tr><td>Total fuel cost</td><td className="rpt-warn">${fmt(Math.round(r.heliTotalFuelCost))}</td><td className="rpt-good">${fmt(Math.round(r.aircatTotalFuelCost))}</td></tr>
                      
                      <tr><td>Standby + charter</td><td className="rpt-warn">${fmt(Math.round(r.heliStandbyCost))} standby + ${fmt(Math.round(r.heliCharterCost))} ({fmtD(r.heliBillableHrs, 1)} billable hrs × ${fmt(fr.heliHourlyRate)}/hr)</td><td className="rpt-good">${fmt(fr.aircatDayRate)}/day</td></tr>
                      <tr><td style={{ fontWeight: 700 }}>Daily total cost</td><td className={fWins ? "rpt-warn" : "rpt-good"} style={{ fontWeight: 700 }}>${fmt(Math.round(r.heliDailyTotal))}</td><td className={fWins ? "rpt-good" : "rpt-warn"} style={{ fontWeight: 700 }}>${fmt(Math.round(r.aircatDailyTotal))}</td></tr>
                      <tr><td style={{ fontWeight: 700 }}>Cost per passenger</td><td className={fWins ? "rpt-warn" : "rpt-good"} style={{ fontWeight: 700 }}>${fmtD(r.heliCostPerPax)}/pax</td><td className={fWins ? "rpt-good" : "rpt-warn"} style={{ fontWeight: 700 }}>${fmtD(r.aircatCostPerPax)}/pax</td></tr>
                      <tr><td>CO₂ per pax</td><td className="rpt-warn">{fmt(Math.round(r.heliCO2PerPax))} kg</td><td className="rpt-good">{fmt(Math.round(r.aircatCO2PerPax))} kg</td></tr>
                    </tbody>
                  </table>
                </RptSection>

                <RptSection title={`${multiField ? `Field ${fi + 1}: ` : ""}Financial Summary`}>
                  <div style={{ background: fWins ? "linear-gradient(135deg,#E1F5EE,#d0f0e0)" : "linear-gradient(135deg,#FEF3C7,#FDE68A)", borderRadius: 8, padding: "14px 18px", margin: "12px 0" }}>
                    <div style={{ fontSize: 12, color: fWins ? "#0F6E56" : "#92400E", marginBottom: 4, fontWeight: 600 }}>
                      {fWins ? `AIRCAT 35 delivers ${fSavPct}% lower cost per passenger vs ${heli.name}` : `${heli.name} is ${fSavPct}% cheaper per pax in this scenario`}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: fWins ? "#085041" : "#78350F", fontFamily: "monospace" }}>
                      {fWins ? `Saving $${fmtD(r.heliCostPerPax - r.aircatCostPerPax)}/pax — $${fmtM(r.annualSaving / 1_000_000)}M/year over the lowest performing asset` : `${heli.name}: $${fmtD(r.heliCostPerPax)}/pax vs AIRCAT: $${fmtD(r.aircatCostPerPax)}/pax`}
                    </div>
                  </div>
                </RptSection>
              </div>
            );
          })}

          {/* Vessel-to-Vessel head-to-head */}
          {showVessel && vesselComparisons.length > 0 && (
            <RptSection title="Vessel-to-Vessel Comparison — AIRCAT 35 vs Benchmarked CTVs">
              <p className="rpt-p" style={{ marginBottom: 12 }}>
                Head-to-head analysis of the AIRCAT 35 Crewliner against each benchmarked crew transfer vessel, using the same fields, passenger loads, MGO price (${fmt(mgoPriceMT)}/MT) and {opsDays} operating days per year configured in this scenario.
              </p>
              {vesselComparisons.map(vc => (
                <div key={vc.key} style={{ marginBottom: 18 }}>
                  <h4 style={{ fontSize: 12, fontWeight: 700, color: "#1D2F5C", marginBottom: 6 }}>AIRCAT 35 vs {vc.vessel.name}</h4>
                  <table className="rpt-tbl">
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th style={{ color: VO_GREEN }}>AIRCAT 35 (Velocity)</th>
                        <th>{vc.vessel.name}</th>
                        <th>Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>Hull type</td><td>SES aluminium catamaran</td><td>{vc.vessel.hullType}</td><td>—</td></tr>
                      <tr><td>Passenger capacity</td><td>{AIRCAT_PAX} pax</td><td>{vc.vessel.pax} pax</td><td>{AIRCAT_PAX - vc.vessel.pax > 0 ? "+" : ""}{AIRCAT_PAX - vc.vessel.pax} pax</td></tr>
                      <tr><td>Cruise speed</td><td>{AIRCAT_SPEED_KTS} kts</td><td>{vc.vessel.kts} kts</td><td>{AIRCAT_SPEED_KTS - vc.vessel.kts > 0 ? "+" : ""}{AIRCAT_SPEED_KTS - vc.vessel.kts} kts</td></tr>
                      <tr><td>MGO burn</td><td>{AIRCAT_MGO_BURN_MT_HR} MT/hr</td><td>{vc.vessel.mgoBurnMTHr} MT/hr</td><td>—</td></tr>
                      <tr><td>Day rate</td><td>${fmt(agg.fieldResults[0]?.aircatDayRate ?? 9500)}/day</td><td>${fmt(vesselDayRateOverrides[vc.key] ?? vc.vessel.dayRate)}/day{vesselDayRateOverrides[vc.key] !== undefined ? " *" : ""}</td><td>${fmt(Math.abs((vesselDayRateOverrides[vc.key] ?? vc.vessel.dayRate) - (agg.fieldResults[0]?.aircatDayRate ?? 9500)))}/day</td></tr>
                      <tr><td>Combined daily cost (all fields)</td><td className={vc.dailyDelta > 0 ? "rpt-good" : "rpt-warn"}>${fmt(Math.round(agg.totalAircatDaily))}</td><td className={vc.dailyDelta > 0 ? "rpt-warn" : "rpt-good"}>${fmt(Math.round(vc.totalDaily))}</td><td className={vc.dailyDelta > 0 ? "rpt-good" : "rpt-warn"}>{vc.dailyDelta > 0 ? "−" : "+"}${fmt(Math.round(Math.abs(vc.dailyDelta)))}/day</td></tr>
                      <tr style={{ fontWeight: 700 }}><td>Cost per passenger</td><td className={vc.pct > 0 ? "rpt-good" : "rpt-warn"}>${fmtD(agg.avgAircatCostPerPax)}/pax</td><td className={vc.pct > 0 ? "rpt-warn" : "rpt-good"}>${fmtD(vc.avgCostPerPax)}/pax</td><td className={vc.pct > 0 ? "rpt-good" : "rpt-warn"}>{Math.abs(vc.pct)}% {vc.pct > 0 ? "lower" : "higher"}</td></tr>
                      <tr style={{ fontWeight: 700 }}><td>Annual cost ({opsDays} days)</td><td className={vc.annualDelta > 0 ? "rpt-good" : "rpt-warn"}>${fmtM(agg.totalAircatAnnual / 1_000_000)}M</td><td className={vc.annualDelta > 0 ? "rpt-warn" : "rpt-good"}>${fmtM(vc.totalAnnual / 1_000_000)}M</td><td className={vc.annualDelta > 0 ? "rpt-good" : "rpt-warn"}>{vc.annualDelta > 0 ? "Saving " : "Premium "}${fmtM(Math.abs(vc.annualDelta) / 1_000_000)}M/yr</td></tr>
                    </tbody>
                  </table>

                  {fieldBlocks.length > 0 && (
                    <table className="rpt-tbl" style={{ marginTop: 8 }}>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Pax</th>
                          <th style={{ color: VO_GREEN }}>AIRCAT $/pax</th>
                          <th>{vc.vessel.name} $/pax</th>
                          <th>Delta $/pax</th>
                          <th>Saving/day</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vc.fieldCalcs.map((fc, fi) => {
                          const fr = agg.fieldResults[fi];
                          const dPax = fc.costPerPax - (fr?.r.aircatCostPerPax ?? 0);
                          const dDay = fc.dailyTotal - (fr?.r.aircatDailyTotal ?? 0);
                          return (
                            <tr key={fi}>
                              <td style={{ fontWeight: 600 }}>{fieldBlocks[fi]?.fieldName || fieldBlocks[fi]?.name || `Field ${fi + 1}`}</td>
                              <td>{fieldPaxList[fi]}</td>
                              <td className={dPax > 0 ? "rpt-good" : "rpt-warn"}>${fmtD(fr?.r.aircatCostPerPax ?? 0)}</td>
                              <td className={dPax > 0 ? "rpt-warn" : "rpt-good"}>${fmtD(fc.costPerPax)}</td>
                              <td className={dPax > 0 ? "rpt-good" : "rpt-warn"}>{dPax > 0 ? "−" : "+"}${fmtD(Math.abs(dPax))}</td>
                              <td className={dDay > 0 ? "rpt-good" : "rpt-warn"}>{dDay > 0 ? "−" : "+"}${fmt(Math.round(Math.abs(dDay)))}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
              {Object.keys(vesselDayRateOverrides).length > 0 && (
                <p className="rpt-p" style={{ fontSize: 10, fontStyle: "italic" }}>* Custom contract day rate entered by client (differs from industry default).</p>
              )}
            </RptSection>
          )}

          {/* Competitor Vessel Comparison (if any selected) */}

          {selectedVesselResults.length > 0 && (
            <RptSection title="Competitor Vessel Comparison">
              <p className="rpt-p" style={{ marginBottom: 12 }}>The following competitor CTVs were included for benchmarking. Custom contract rates are marked where applied.</p>
              <table className="rpt-tbl">
                <thead>
                  <tr>
                    <th>Vessel</th>
                    <th>Pax</th>
                    <th>Speed</th>
                    <th>Day Rate</th>
                    <th>Avg $/pax</th>
                    <th>Annual Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {/* AIRCAT row first */}
                  <tr style={{ background: "#E1F5EE" }}>
                    <td style={{ fontWeight: 700 }}>AIRCAT 35 (Velocity Offshore) ✓</td>
                    <td>{AIRCAT_PAX} pax</td>
                    <td>{AIRCAT_SPEED_KTS} kts</td>
                    <td>${fmt(agg.fieldResults[0]?.aircatDayRate ?? 9500)}/day</td>
                    <td className="rpt-good" style={{ fontWeight: 700 }}>${fmtD(agg.avgAircatCostPerPax)}</td>
                    <td>${fmtM(agg.totalAircatAnnual / 1_000_000)}M</td>
                  </tr>
                  {selectedVesselResults.map((svr) => {
                    const isCustom = vesselDayRateOverrides[svr.key] !== undefined;
                    const effectiveRate = vesselDayRateOverrides[svr.key] ?? svr.vessel.dayRate;
                    return (
                      <tr key={svr.key}>
                        <td style={{ fontWeight: 600 }}>{svr.vessel.name}</td>
                        <td>{svr.vessel.pax} pax</td>
                        <td>{svr.vessel.kts} kts</td>
                        <td>${fmt(effectiveRate)}/day{isCustom ? ' *' : ''}</td>
                        <td>${fmtD(svr.avgCostPerPax)}</td>
                        <td>${fmtM(svr.totalAnnual / 1_000_000)}M</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {Object.keys(vesselDayRateOverrides).length > 0 && (
                <p className="rpt-p" style={{ fontSize: 10, fontStyle: "italic", marginTop: 6 }}>* Custom contract day rate entered by client (differs from industry default).</p>
              )}

              {/* Per-field breakdown for each competitor vessel */}
              {fieldBlocks.length > 0 && selectedVesselResults.map((svr) => {
                const effectiveRate = vesselDayRateOverrides[svr.key] ?? svr.vessel.dayRate;
                return (
                  <div key={svr.key} style={{ marginTop: 16 }}>
                    <h4 style={{ fontSize: 12, fontWeight: 700, color: "#1D2F5C", marginBottom: 6 }}>{svr.vessel.name} — Per-Field Breakdown</h4>
                    <table className="rpt-tbl">
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Dist</th>
                          <th>Pax</th>
                          <th>Trips</th>
                          <th>Fuel Cost</th>
                          <th>Daily Total</th>
                          <th>$/pax</th>
                          <th>CO₂/pax</th>
                        </tr>
                      </thead>
                      <tbody>
                        {svr.fieldCalcs.map((fc, fi) => {
                          const fb = fieldBlocks[fi];
                          const fp = fieldPaxList[fi];
                          return (
                            <tr key={fi}>
                              <td style={{ fontWeight: 600 }}>{fb?.fieldName || fb?.name || `Field ${fi + 1}`}</td>
                              <td>{fb?.nm} nm</td>
                              <td>{fp}</td>
                              <td>{fc.trips}</td>
                              <td>${fmt(Math.round(fc.totalFuelCost))}</td>
                              <td>${fmt(Math.round(fc.dailyTotal))}</td>
                              <td style={{ fontWeight: 700 }}>${fmtD(fc.costPerPax)}</td>
                              <td>{fmt(Math.round(fc.co2PerPax))} kg</td>
                            </tr>
                          );
                        })}
                        <tr style={{ fontWeight: 700, borderTop: "2px solid #1D2F5C" }}>
                          <td>AVERAGE</td>
                          <td>—</td>
                          <td>—</td>
                          <td>—</td>
                          <td>—</td>
                          <td>${fmt(Math.round(svr.totalDaily))}/day</td>
                          <td>${fmtD(svr.avgCostPerPax)}</td>
                          <td>—</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="rpt-p" style={{ fontSize: 10, marginTop: 4 }}>Day rate: ${fmt(effectiveRate)}/day{vesselDayRateOverrides[svr.key] !== undefined ? ' (custom)' : ''} · Annual: ${fmtM(svr.totalAnnual / 1_000_000)}M ({opsDays} ops days)</p>
                  </div>
                );
              })}
            </RptSection>
          )}

          {/* Helicopter Rate Comparison (all helicopters) */}
          {allHeliResults.length > 0 && (
            <RptSection title="Helicopter Rate Comparison">
              <table className="rpt-tbl">
                <thead>
                  <tr>
                    <th>Helicopter</th>
                    <th>Pax</th>
                    <th>Speed</th>
                    <th>Hourly Rate</th>
                    <th>Avg $/pax</th>
                    <th>Annual Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {allHeliResults.map((hr) => {
                    const isCustom = heliRateOverrides[hr.key] !== undefined;
                    const effectiveRate = heliRateOverrides[hr.key] ?? hr.heli.hourlyRate;
                    return (
                      <tr key={hr.key}>
                        <td style={{ fontWeight: 600 }}>{hr.heli.name}</td>
                        <td>{hr.heli.pax} pax</td>
                        <td>{hr.heli.kts} kts</td>
                        <td>${fmt(effectiveRate)}/hr{isCustom ? ' *' : ''}</td>
                        <td className="rpt-warn">${fmtD(hr.avgCostPerPax)}</td>
                        <td>${fmtM(hr.totalAnnual / 1_000_000)}M</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {Object.keys(heliRateOverrides).length > 0 && (
                <p className="rpt-p" style={{ fontSize: 10, fontStyle: "italic", marginTop: 6 }}>* Custom contract hourly rate entered by client (differs from industry default).</p>
              )}

              {/* Per-field breakdown for each helicopter */}
              {fieldBlocks.length > 0 && allHeliResults.map((hr) => {
                const effectiveRate = heliRateOverrides[hr.key] ?? hr.heli.hourlyRate;
                return (
                  <div key={hr.key} style={{ marginTop: 16 }}>
                    <h4 style={{ fontSize: 12, fontWeight: 700, color: "#C53030", marginBottom: 6 }}>{hr.heli.name} — Per-Field Breakdown</h4>
                    <table className="rpt-tbl">
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Dist</th>
                          <th>Pax</th>
                          <th>Flights</th>
                          <th>Fuel Cost</th>
                          <th>Daily Total</th>
                          <th>$/pax</th>
                          <th>CO₂/pax</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hr.fieldCalcs.map((fc, fi) => {
                          const fb = fieldBlocks[fi];
                          const fp = fieldPaxList[fi];
                          return (
                            <tr key={fi}>
                              <td style={{ fontWeight: 600 }}>{fb?.fieldName || fb?.name || `Field ${fi + 1}`}</td>
                              <td>{fb?.nm} nm</td>
                              <td>{fp}</td>
                              <td>{fc.heliFlights}</td>
                              <td>${fmt(Math.round(fc.heliTotalFuelCost))}</td>
                              <td>${fmt(Math.round(fc.heliDailyTotal))}</td>
                              <td style={{ fontWeight: 700 }}>${fmtD(fc.heliCostPerPax)}</td>
                              <td>{fmt(Math.round(fc.heliCO2PerPax))} kg</td>
                            </tr>
                          );
                        })}
                        <tr style={{ fontWeight: 700, borderTop: "2px solid #C53030" }}>
                          <td>AVERAGE</td>
                          <td>—</td>
                          <td>—</td>
                          <td>—</td>
                          <td>—</td>
                          <td>${fmt(Math.round(hr.totalDaily))}/day</td>
                          <td>${fmtD(hr.avgCostPerPax)}</td>
                          <td>—</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="rpt-p" style={{ fontSize: 10, marginTop: 4 }}>Hourly rate: ${fmt(effectiveRate)}/hr{heliRateOverrides[hr.key] !== undefined ? ' (custom)' : ''} · Annual: ${fmtM(hr.totalAnnual / 1_000_000)}M ({opsDays} ops days)</p>
                  </div>
                );
              })}
            </RptSection>
          )}

          {/* Dynamic Recommendation Summary */}
          {(() => {
            const allOptions: { name: string; costPerPax: number; type: string }[] = [
              { name: "AIRCAT 35 (Velocity)", costPerPax: agg.avgAircatCostPerPax, type: "CTV" },
              ...allHeliResults.map(hr => ({ name: hr.heli.name, costPerPax: hr.avgCostPerPax, type: "Helicopter" })),
              ...selectedVesselResults.map(svr => ({ name: svr.vessel.name, costPerPax: svr.avgCostPerPax, type: "CTV" })),
            ];
            const best = allOptions.reduce((a, b) => a.costPerPax < b.costPerPax ? a : b);
            const second = allOptions.filter(o => o.name !== best.name).reduce((a, b) => a.costPerPax < b.costPerPax ? a : b, { name: '', costPerPax: Infinity, type: '' });
            const savVsSecond = second.costPerPax > 0 ? Math.round((1 - best.costPerPax / second.costPerPax) * 100) : 0;
            return (
              <RptSection title="Analysis Recommendation">
                <div style={{ background: "linear-gradient(135deg,#E1F5EE,#d0f0e0)", borderRadius: 8, padding: "18px 22px", margin: "12px 0", border: "2px solid #1D9E75" }}>
                  <div style={{ fontSize: 11, color: "#0F6E56", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>✓ Recommended Mode of Transport</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: "#085041", fontFamily: "monospace" }}>{best.name}</div>
                  <div style={{ fontSize: 13, color: "#0F6E56", marginTop: 6 }}>
                    Lowest cost per passenger at <strong>${fmtD(best.costPerPax)}/pax</strong>
                    {savVsSecond > 0 && <> — {savVsSecond}% cheaper than next best ({second.name} at ${fmtD(second.costPerPax)}/pax)</>}
                  </div>
                </div>
                <table className="rpt-tbl" style={{ marginTop: 12 }}>
                  <thead><tr><th>#</th><th>Transport Mode</th><th>Type</th><th>Avg $/pax</th><th>Status</th></tr></thead>
                  <tbody>
                    {allOptions.sort((a, b) => a.costPerPax - b.costPerPax).map((o, i) => (
                      <tr key={i} style={o.name === best.name ? { background: "#E1F5EE" } : {}}>
                        <td>{i + 1}</td>
                        <td style={{ fontWeight: o.name === best.name ? 700 : 400 }}>{o.name}</td>
                        <td>{o.type}</td>
                        <td style={{ fontWeight: 700 }}>${fmtD(o.costPerPax)}</td>
                        <td>{o.name === best.name ? <span className="rpt-badge-green">✓ RECOMMENDED</span> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="rpt-p" style={{ fontSize: 10, fontStyle: "italic", marginTop: 8 }}>Recommendation is based on the lowest average cost per passenger across all selected fields, using current fuel prices and configured rates. Additional operational factors (safety, weather availability, scheduling) should be considered in final decision-making.</p>
              </RptSection>
            );
          })()}

          {/* CTV-Led Crew Logistics */}
          <RptSection title="CTV-Led Crew Logistics — Operational Advantages">
            <table className="rpt-tbl">
              <thead><tr><th>Function</th><th>Primary Mode</th><th>Rationale</th></tr></thead>
              <tbody>
                <tr><td>Routine crew change</td><td><span className="rpt-badge-green">CTV (AIRCAT 35)</span></td><td>{AIRCAT_PAX} passengers + cargo per voyage. Multi-FPSO routing. Significantly lower cost per passenger transferred.</td></tr>
                <tr><td>Emergency medevac</td><td><span className="rpt-badge-red">Helicopter</span></td><td>Speed-critical response. CTV provides backup when helicopter is weather-limited.</td></tr>
                <tr><td>SAR standby</td><td><span className="rpt-badge-green">CTV (AIRCAT 35)</span></td><td>Regulatory requirement. Factory-fitted SEA.AI SENTRY thermal detection system and MOB recovery davit.</td></tr>
                <tr><td>Cargo & equipment</td><td><span className="rpt-badge-green">CTV (AIRCAT 35)</span></td><td>Eliminates separate supply vessel for routine cargo.</td></tr>
                <tr><td>Bad weather backup</td><td>Cross-redundancy</td><td>CTV operates when helicopter grounded by visibility restrictions. CTV expected 98%+ weather availability.</td></tr>
              </tbody>
            </table>
          </RptSection>

          {/* Safety */}
          <RptSection title="Safety Advantages of CTV-Led Operations">
            <table className="rpt-tbl">
              <thead><tr><th>Risk Factor</th><th>Helicopter</th><th>AIRCAT 35</th></tr></thead>
              <tbody>
                <tr><td>Transfer mechanism</td><td className="rpt-warn">Winch / hoist risk (helideck operations)</td><td className="rpt-good">Alongside transfer — no hoist risk</td></tr>
                <tr><td>Failure mode</td><td className="rpt-warn">Catastrophic in power/mechanical failure</td><td className="rpt-good">Vessel remains buoyant in all failure modes</td></tr>
              </tbody>
            </table>
          </RptSection>

          {/* AIRCAT 35 Specs */}
          <RptSection title="About the AIRCAT 35 Crewliner">
            <RptTable headers={["Specification", "Detail"]} rows={[
              ["Vessel type", "Surface Effect Ship (SES) aluminium catamaran — Crew Transfer Vessel (CTV)"],
              ["Builder", "Strategic Marine (Singapore / Henderson, WA)"],
              ["Passenger capacity", `${AIRCAT_PAX} industrial personnel`],
              ["Maximum speed", `${AIRCAT_MAX_SPEED} knots`],
              ["Cruise speed", `${AIRCAT_SPEED_KTS} knots`],
              ["Propulsion", "2× MTU 16V 2000 M96L diesel · Kongsberg waterjets"],
              ["Fuel", "MGO (Marine Gas Oil)"],
              ["Fuel consumption", `${AIRCAT_MGO_BURN_MT_HR} MT/hr`],
              ["Classification", "ABS High Speed Craft (HSC)"],
              ["Safety systems", "SEA.AI SENTRY (2× day + 2× FLIR thermal cameras), MOB recovery davit"],
            ]} />
          </RptSection>

          {/* Report footer */}
          <div style={{ background: "#1D2F5C", color: "#fff", padding: "14px 32px", fontSize: 11, display: "flex", justifyContent: "space-between", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" as any }}>
            <span>Velocity Offshore Holdings LLC — Marshall Islands Incorporated — CONFIDENTIAL</span>
            <span>VOH-FUEL-RPT-001 · {date}</span>
          </div>
        </div>

        {/* Modal action buttons */}
        <div id="report-print-actions" className="p-4 border-t border-border flex flex-wrap items-center gap-3" style={{ background: "#f5f4f0" }}>
          <div className="flex items-center gap-2 mr-auto">
            <span style={{ fontSize: 12, color: "#4b5563", fontWeight: 600 }}>Report scope:</span>
            <div className="flex rounded-lg overflow-hidden" style={{ border: "1px solid #d3d1c7" }}>
              {([
                { k: "combined", l: "Combined" },
                { k: "heli", l: "Heli vs Vessel" },
                { k: "vessel", l: "Vessel vs Vessel" },
              ] as const).map(o => (
                <button key={o.k} onClick={() => setScope(o.k)}
                  style={{ padding: "6px 12px", fontSize: 12, cursor: "pointer", border: "none", background: scope === o.k ? "#1D2F5C" : "#fff", color: scope === o.k ? "#fff" : "#1D2F5C", fontWeight: scope === o.k ? 600 : 400 }}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => window.print()} style={{ padding: "7px 16px", background: "#1D2F5C", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer", fontWeight: 500 }}>Print / Save as PDF</button>
          <button onClick={() => handlePdfDownload()} style={{ padding: "7px 16px", background: "#1D9E75", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer", fontWeight: 500 }}>⬇ Download PDF</button>
          <button onClick={onClose} style={{ padding: "7px 16px", background: "#fff", border: "1px solid #d3d1c7", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>Close</button>
        </div>
      </div>
    </div>
  );

  // ── jsPDF download handler ──
  function handlePdfDownload() {
    import('jspdf').then(({ default: jsPDF }) => {
      import('jspdf-autotable').then((autoTableModule) => {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pw = doc.internal.pageSize.getWidth();
        const margin = 15;
        let y = 15;

        const addPage = () => { doc.addPage(); y = 15; };
        const checkPage = (need: number) => { if (y + need > 275) addPage(); };

        // ── Header ──
        doc.setFillColor(29, 47, 92);
        doc.rect(0, 0, pw, 35, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('Velocity Offshore', margin, 14);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text('Offshore Marine Transportation & Support Services', margin, 20);
        doc.setFontSize(8);
        doc.text(`Report: ${multiField ? 'Multi-Field Portfolio Analysis' : 'Crew Transfer Cost Analysis'} | Date: ${date}`, margin, 28);
        doc.text(`Helicopter baseline: ${heli.name} | CTV: AIRCAT 35 (Velocity Offshore)`, margin, 33);
        y = 42;

        // CONFIDENTIAL
        doc.setTextColor(200, 0, 0);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('CONFIDENTIAL', pw - margin, 42, { align: 'right' });
        doc.setTextColor(0, 0, 0);
        y = 48;

        // ── Executive Savings Summary (page 1) ──
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(29, 47, 92);
        doc.text(`Executive Summary — Savings at a Glance (${scopeLabel})`, margin, y);
        y += 6;

        doc.setFillColor(15, 110, 86);
        doc.roundedRect(margin, y, pw - margin * 2, 22, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text('TOTAL IDENTIFIED ANNUAL SAVING', margin + 4, y + 7);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`$${fmtM(Math.max(0, headlineSaving) / 1_000_000)}M / year`, margin + 4, y + 16);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(`${agg.fieldResults.length} field(s) · ${agg.totalPax} pax/day · ${opsDays} ops days/yr`, pw - margin - 4, y + 16, { align: 'right' });
        y += 27;
        doc.setTextColor(0, 0, 0);

        (doc as any).autoTable({
          startY: y,
          margin: { left: margin, right: margin },
          head: [['Comparison', 'Benchmark', 'Benchmark $/pax', 'AIRCAT 35 $/pax', 'Saving/day', 'Saving/yr']],
          body: [
            ...(showHeli ? [['Vessel vs Helicopter', heli.name, `$${fmtD(agg.avgHeliCostPerPax)}`, `$${fmtD(agg.avgAircatCostPerPax)}`,
              `$${fmt(Math.round(Math.abs(heliDailySaving)))}`, `$${fmtM(Math.abs(heliAnnualSaving) / 1_000_000)}M`]] : []),
            ...(showVessel ? vesselComparisons.map(vc => [
              'Vessel vs Vessel', vc.vessel.name, `$${fmtD(vc.avgCostPerPax)}`, `$${fmtD(agg.avgAircatCostPerPax)}`,
              `$${fmt(Math.round(Math.abs(vc.dailyDelta)))}`, `$${fmtM(Math.abs(vc.annualDelta) / 1_000_000)}M`,
            ]) : []),
          ],
          headStyles: { fillColor: [29, 47, 92], fontSize: 7 },
          bodyStyles: { fontSize: 7 },
          theme: 'grid',
        });
        y = (doc as any).lastAutoTable.finalY + 8;


        // ── Portfolio Summary ──
        if (showHeli && multiField) {
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(29, 47, 92);
          doc.text('Portfolio Summary', margin, y);
          y += 6;

          const summaryBanner = aircatWins
            ? `AIRCAT 35 delivers ${savPct}% lower average cost/pax — saving $${fmtM(Math.abs(agg.totalAnnualSaving) / 1_000_000)}M/year over the lowest performing asset`
            : `${heli.name} is ${savPct}% cheaper on average`;
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
          doc.text(summaryBanner, margin, y);
          y += 6;

          (doc as any).autoTable({
            startY: y,
            margin: { left: margin, right: margin },
            head: [['Field', 'Dist', 'Pax', 'Heli $/pax', 'AIRCAT $/pax', 'Saving/day', 'Saving/yr']],
            body: agg.fieldResults.map(fr => [
              fr.block.fieldName || fr.block.name,
              `${fr.distance} nm`,
              fr.pax,
              `$${fmtD(fr.r.heliCostPerPax)}`,
              `$${fmtD(fr.r.aircatCostPerPax)}`,
              `$${fmt(Math.round(Math.abs(fr.r.dailySaving)))}`,
              `$${fmtM(Math.abs(fr.r.annualSaving) / 1_000_000)}M`,
            ]),
            headStyles: { fillColor: [29, 47, 92], fontSize: 7 },
            bodyStyles: { fontSize: 7 },
            theme: 'grid',
          });
          y = (doc as any).lastAutoTable.finalY + 8;
        }

        // ── Per-field detail ──
        (showHeli ? agg.fieldResults : []).forEach((fr, fi) => {
          checkPage(60);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(29, 47, 92);
          doc.text(`${multiField ? `Field ${fi + 1}: ` : ''}${fr.block.fieldName || fr.block.name}`, margin, y);
          y += 6;

          (doc as any).autoTable({
            startY: y,
            margin: { left: margin, right: margin },
            head: [['Parameter', heli.name, 'AIRCAT 35']],
            body: [
              ['Capacity', `${heli.pax} pax`, `${AIRCAT_PAX} pax`],
              ['Round trips', `${fr.r.heliFlights} flights`, `${fr.r.aircatTrips} voyage(s)`],
              ['RT time', toHHMM(fr.r.heliRTHrs), toHHMM(fr.r.aircatRTHrs)],
              ['Total time', toHHMM(fr.r.heliRTHrs * fr.r.heliFlights), toHHMM(fr.r.aircatRTHrs * fr.r.aircatTrips)],
              ['Fuel cost', `$${fmt(Math.round(fr.r.heliTotalFuelCost))}`, `$${fmt(Math.round(fr.r.aircatTotalFuelCost))}`],
              
              ['Charter/day rate', `$${fmt(Math.round(fr.r.heliCharterCost))}`, `$${fmt(fr.aircatDayRate)}/day`],
              ['Daily total', `$${fmt(Math.round(fr.r.heliDailyTotal))}`, `$${fmt(Math.round(fr.r.aircatDailyTotal))}`],
              ['Cost/pax', `$${fmtD(fr.r.heliCostPerPax)}`, `$${fmtD(fr.r.aircatCostPerPax)}`],
              ['CO2/pax', `${fmt(Math.round(fr.r.heliCO2PerPax))} kg`, `${fmt(Math.round(fr.r.aircatCO2PerPax))} kg`],
            ],
            headStyles: { fillColor: [29, 47, 92], fontSize: 7 },
            bodyStyles: { fontSize: 7 },
            columnStyles: { 1: { textColor: [197, 48, 48] }, 2: { textColor: [29, 158, 117] } },
            theme: 'grid',
          });
          y = (doc as any).lastAutoTable.finalY + 8;
        });

        // ── Vessel-to-Vessel head-to-head ──
        if (showVessel && vesselComparisons.length > 0) {
          checkPage(45);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(29, 47, 92);
          doc.text('Vessel-to-Vessel Comparison — AIRCAT 35 vs Benchmarked CTVs', margin, y);
          y += 6;

          vesselComparisons.forEach(vc => {
            checkPage(55);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(29, 47, 92);
            doc.text(`AIRCAT 35 vs ${vc.vessel.name}`, margin, y);
            y += 5;

            (doc as any).autoTable({
              startY: y,
              margin: { left: margin, right: margin },
              head: [['Parameter', 'AIRCAT 35 (Velocity)', vc.vessel.name, 'Delta']],
              body: [
                ['Hull type', 'SES aluminium catamaran', vc.vessel.hullType, '—'],
                ['Capacity', `${AIRCAT_PAX} pax`, `${vc.vessel.pax} pax`, `${AIRCAT_PAX - vc.vessel.pax > 0 ? '+' : ''}${AIRCAT_PAX - vc.vessel.pax} pax`],
                ['Cruise speed', `${AIRCAT_SPEED_KTS} kts`, `${vc.vessel.kts} kts`, `${AIRCAT_SPEED_KTS - vc.vessel.kts > 0 ? '+' : ''}${AIRCAT_SPEED_KTS - vc.vessel.kts} kts`],
                ['Day rate', `$${fmt(agg.fieldResults[0]?.aircatDayRate ?? 9500)}/day`, `$${fmt(vesselDayRateOverrides[vc.key] ?? vc.vessel.dayRate)}/day`, `$${fmt(Math.abs((vesselDayRateOverrides[vc.key] ?? vc.vessel.dayRate) - (agg.fieldResults[0]?.aircatDayRate ?? 9500)))}/day`],
                ['Daily total (all fields)', `$${fmt(Math.round(agg.totalAircatDaily))}`, `$${fmt(Math.round(vc.totalDaily))}`, `${vc.dailyDelta > 0 ? '-' : '+'}$${fmt(Math.round(Math.abs(vc.dailyDelta)))}/day`],
                ['Cost/pax', `$${fmtD(agg.avgAircatCostPerPax)}`, `$${fmtD(vc.avgCostPerPax)}`, `${Math.abs(vc.pct)}% ${vc.pct > 0 ? 'lower' : 'higher'}`],
                [`Annual (${opsDays} days)`, `$${fmtM(agg.totalAircatAnnual / 1_000_000)}M`, `$${fmtM(vc.totalAnnual / 1_000_000)}M`, `${vc.annualDelta > 0 ? 'Saving ' : 'Premium '}$${fmtM(Math.abs(vc.annualDelta) / 1_000_000)}M/yr`],
              ],
              headStyles: { fillColor: [29, 47, 92], fontSize: 7 },
              bodyStyles: { fontSize: 7 },
              columnStyles: { 1: { textColor: [29, 158, 117] } },
              theme: 'grid',
            });
            y = (doc as any).lastAutoTable.finalY + 4;

            if (fieldBlocks.length > 0) {
              (doc as any).autoTable({
                startY: y,
                margin: { left: margin, right: margin },
                head: [['Field', 'Pax', 'AIRCAT $/pax', `${vc.vessel.name} $/pax`, 'Delta $/pax', 'Saving/day']],
                body: vc.fieldCalcs.map((fc, fi) => {
                  const fr = agg.fieldResults[fi];
                  const dPax = fc.costPerPax - (fr?.r.aircatCostPerPax ?? 0);
                  const dDay = fc.dailyTotal - (fr?.r.aircatDailyTotal ?? 0);
                  return [
                    fieldBlocks[fi]?.fieldName || fieldBlocks[fi]?.name || `Field ${fi + 1}`,
                    fieldPaxList[fi],
                    `$${fmtD(fr?.r.aircatCostPerPax ?? 0)}`,
                    `$${fmtD(fc.costPerPax)}`,
                    `${dPax > 0 ? '-' : '+'}$${fmtD(Math.abs(dPax))}`,
                    `${dDay > 0 ? '-' : '+'}$${fmt(Math.round(Math.abs(dDay)))}`,
                  ];
                }),
                headStyles: { fillColor: [29, 158, 117], fontSize: 6.5 },
                bodyStyles: { fontSize: 6.5 },
                theme: 'grid',
              });
              y = (doc as any).lastAutoTable.finalY + 8;
            }
          });
        }

        // ── Competitor Vessels ──

        if (selectedVesselResults.length > 0) {
          checkPage(40);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(29, 47, 92);
          doc.text('Competitor Vessel Comparison', margin, y);
          y += 6;

          (doc as any).autoTable({
            startY: y,
            margin: { left: margin, right: margin },
            head: [['Vessel', 'Pax', 'Speed', 'Day Rate', 'Avg $/pax', 'Annual']],
            body: [
              ['AIRCAT 35 (Velocity) ✓', `${AIRCAT_PAX}`, `${AIRCAT_SPEED_KTS} kts`, `$${fmt(agg.fieldResults[0]?.aircatDayRate ?? 9500)}/day`, `$${fmtD(agg.avgAircatCostPerPax)}`, `$${fmtM(agg.totalAircatAnnual / 1_000_000)}M`],
              ...selectedVesselResults.map(svr => {
                const isCustom = vesselDayRateOverrides[svr.key] !== undefined;
                const effectiveRate = vesselDayRateOverrides[svr.key] ?? svr.vessel.dayRate;
                return [svr.vessel.name, `${svr.vessel.pax}`, `${svr.vessel.kts} kts`, `$${fmt(effectiveRate)}/day${isCustom ? ' *' : ''}`, `$${fmtD(svr.avgCostPerPax)}`, `$${fmtM(svr.totalAnnual / 1_000_000)}M`];
              }),
            ],
            headStyles: { fillColor: [29, 47, 92], fontSize: 7 },
            bodyStyles: { fontSize: 7 },
            theme: 'grid',
          });
          y = (doc as any).lastAutoTable.finalY + 4;
          if (Object.keys(vesselDayRateOverrides).length > 0) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(100, 100, 100);
            doc.text('* Custom contract day rate entered by client.', margin, y);
            y += 6;
          }

          // Per-field breakdown for each competitor vessel
          selectedVesselResults.forEach(svr => {
            checkPage(50);
            y += 4;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(29, 47, 92);
            doc.text(`${svr.vessel.name} — Per-Field Breakdown`, margin, y);
            y += 5;

            (doc as any).autoTable({
              startY: y,
              margin: { left: margin, right: margin },
              head: [['Field', 'Dist', 'Pax', 'Trips', 'Fuel Cost', 'Daily Total', '$/pax', 'CO2/pax']],
              body: svr.fieldCalcs.map((fc, fi) => {
                const fb = fieldBlocks[fi];
                const fp = fieldPaxList[fi];
                return [
                  fb?.fieldName || fb?.name || `Field ${fi + 1}`,
                  `${fb?.nm} nm`, fp,
                  fc.trips, `$${fmt(Math.round(fc.totalFuelCost))}`,
                  `$${fmt(Math.round(fc.dailyTotal))}`, `$${fmtD(fc.costPerPax)}`,
                  `${fmt(Math.round(fc.co2PerPax))} kg`,
                ];
              }),
              headStyles: { fillColor: [29, 47, 92], fontSize: 6.5 },
              bodyStyles: { fontSize: 6.5 },
              theme: 'grid',
            });
            y = (doc as any).lastAutoTable.finalY + 6;
          });
        }

        // ── Helicopter Rates ──
        if (allHeliResults.length > 0) {
          checkPage(40);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(29, 47, 92);
          doc.text('Helicopter Rate Comparison', margin, y);
          y += 6;

          (doc as any).autoTable({
            startY: y,
            margin: { left: margin, right: margin },
            head: [['Helicopter', 'Pax', 'Speed', 'Hourly Rate', 'Avg $/pax', 'Annual']],
            body: allHeliResults.map(hr => {
              const isCustom = heliRateOverrides[hr.key] !== undefined;
              const effectiveRate = heliRateOverrides[hr.key] ?? hr.heli.hourlyRate;
              return [hr.heli.name, `${hr.heli.pax}`, `${hr.heli.kts} kts`, `$${fmt(effectiveRate)}/hr${isCustom ? ' *' : ''}`, `$${fmtD(hr.avgCostPerPax)}`, `$${fmtM(hr.totalAnnual / 1_000_000)}M`];
            }),
            headStyles: { fillColor: [29, 47, 92], fontSize: 7 },
            bodyStyles: { fontSize: 7 },
            theme: 'grid',
          });
          y = (doc as any).lastAutoTable.finalY + 4;
          if (Object.keys(heliRateOverrides).length > 0) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(100, 100, 100);
            doc.text('* Custom contract hourly rate entered by client.', margin, y);
            y += 6;
          }

          // Per-field breakdown for each helicopter
          allHeliResults.forEach(hr => {
            checkPage(50);
            y += 4;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(197, 48, 48);
            doc.text(`${hr.heli.name} — Per-Field Breakdown`, margin, y);
            y += 5;

            (doc as any).autoTable({
              startY: y,
              margin: { left: margin, right: margin },
              head: [['Field', 'Dist', 'Pax', 'Flights', 'Fuel Cost', 'Daily Total', '$/pax', 'CO2/pax']],
              body: hr.fieldCalcs.map((fc, fi) => {
                const fb = fieldBlocks[fi];
                const fp = fieldPaxList[fi];
                return [
                  fb?.fieldName || fb?.name || `Field ${fi + 1}`,
                  `${fb?.nm} nm`, fp,
                  fc.heliFlights, `$${fmt(Math.round(fc.heliTotalFuelCost))}`,
                  `$${fmt(Math.round(fc.heliDailyTotal))}`, `$${fmtD(fc.heliCostPerPax)}`,
                  `${fmt(Math.round(fc.heliCO2PerPax))} kg`,
                ];
              }),
              headStyles: { fillColor: [197, 48, 48], fontSize: 6.5 },
              bodyStyles: { fontSize: 6.5 },
              theme: 'grid',
            });
            y = (doc as any).lastAutoTable.finalY + 6;
          });
        }

        // ── Recommendation ──
        {
          checkPage(45);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(29, 47, 92);
          doc.text('Analysis Recommendation', margin, y);
          y += 6;

          const allOptions = [
            { name: 'AIRCAT 35 (Velocity)', costPerPax: agg.avgAircatCostPerPax, type: 'CTV' },
            ...allHeliResults.map(hr => ({ name: hr.heli.name, costPerPax: hr.avgCostPerPax, type: 'Helicopter' })),
            ...selectedVesselResults.map(svr => ({ name: svr.vessel.name, costPerPax: svr.avgCostPerPax, type: 'CTV' })),
          ].sort((a, b) => a.costPerPax - b.costPerPax);
          const best = allOptions[0];

          // Recommendation banner
          doc.setFillColor(225, 245, 238);
          doc.roundedRect(margin, y, pw - margin * 2, 14, 2, 2, 'F');
          doc.setDrawColor(29, 158, 117);
          doc.roundedRect(margin, y, pw - margin * 2, 14, 2, 2, 'S');
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(15, 110, 86);
          doc.text(`RECOMMENDED: ${best.name} — $${fmtD(best.costPerPax)}/pax (lowest cost per passenger)`, margin + 4, y + 9);
          y += 18;

          (doc as any).autoTable({
            startY: y,
            margin: { left: margin, right: margin },
            head: [['#', 'Transport Mode', 'Type', 'Avg $/pax', 'Status']],
            body: allOptions.map((o, i) => [
              i + 1, o.name, o.type, `$${fmtD(o.costPerPax)}`,
              o.name === best.name ? '✓ RECOMMENDED' : '—',
            ]),
            headStyles: { fillColor: [29, 47, 92], fontSize: 7 },
            bodyStyles: { fontSize: 7 },
            theme: 'grid',
          });
          y = (doc as any).lastAutoTable.finalY + 8;
        }

        // ── Footer on every page ──
        const totalPages = doc.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          doc.setFillColor(29, 47, 92);
          doc.rect(0, 285, pw, 12, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.text('Velocity Offshore Holdings LLC — CONFIDENTIAL', margin, 291);
          doc.text(`VOH-CTI-RPT — ${date} — Page ${i}/${totalPages}`, pw - margin, 291, { align: 'right' });
        }

        const fileName = `Velocity_CTI_Report_${multiField ? 'Portfolio' : (primaryBlock?.fieldName || primaryBlock?.name || 'Analysis').replace(/[^a-zA-Z0-9]/g, '_')}_${date.replace(/\s/g, '_')}.pdf`;
        doc.save(fileName);
      });
    });
  }
};

// ─── REPORT HELPER COMPONENTS ───────────────────────────────
const RptSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rpt-section" style={{ padding: "20px 32px", borderBottom: "1px solid #e5e4e0" }}>
    <h3 style={{ fontSize: 14, fontWeight: 700, color: "#1D2F5C", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12, paddingBottom: 6, borderBottom: "2px solid #1D9E75" }}>{title}</h3>
    {children}
  </div>
);

const RptTable = ({ headers, rows }: { headers: string[]; rows: string[][] }) => (
  <table className="rpt-tbl">
    <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
    <tbody>{rows.map((row, ri) => <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>)}</tbody>
  </table>
);

// ─── MAIN COMPONENT ─────────────────────────────────────────
const CTI_ACCESS_KEY = "cti_access_granted";

const CrewTransferIntelligence = ({ skipGate = false, skipLayout = false, hideWeatherTab = false, hideReportTab = false }: { skipGate?: boolean; skipLayout?: boolean; hideWeatherTab?: boolean; hideReportTab?: boolean }) => {
  // ── Access Gate State ──
  const [accessGranted, setAccessGranted] = useState(() => {
    if (skipGate) return true;
    try { return localStorage.getItem(CTI_ACCESS_KEY) === "true"; } catch { return false; }
  });
  const [gateForm, setGateForm] = useState({ firstName: "", lastName: "", email: "", company: "" });
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const [gateError, setGateError] = useState("");

  const handleGateSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const { firstName, lastName, email, company } = gateForm;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !company.trim()) {
      setGateError("All fields are required.");
      return;
    }
    setGateSubmitting(true);
    setGateError("");
    try {
      const { error } = await supabase.functions.invoke("notify-cti-access", {
        body: { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), company: company.trim() },
      });
      if (error) throw error;
      localStorage.setItem(CTI_ACCESS_KEY, "true");
      setAccessGranted(true);
    } catch (err: any) {
      setGateError("Something went wrong. Please try again.");
      console.error("CTI access gate error:", err);
    } finally {
      setGateSubmitting(false);
    }
  };

  const [activeTab, setActiveTab] = useState<"prices" | "analysis" | "weather" | "report">("prices");
  const [heliKeys, setHeliKeys] = useState<string[]>(["AW139"]);
  const [heliSelectorOpen, setHeliSelectorOpen] = useState(false);
  const [vesselKeys, setVesselKeys] = useState<string[]>(["ic0618"]);
  const [vesselSelectorOpen, setVesselSelectorOpen] = useState(false);
  const [vesselDayRateOverrides, setVesselDayRateOverrides] = useState<Record<string, number>>({});
  const [dayRateDrafts, setDayRateDrafts] = useState<Record<string, string>>({});
  const [dayRateEditing, setDayRateEditing] = useState<Record<string, boolean>>({});
  const [heliRateOverrides, setHeliRateOverrides] = useState<Record<string, number>>({});
  const [heliRateDrafts, setHeliRateDrafts] = useState<Record<string, string>>({});
  const [heliRateEditing, setHeliRateEditing] = useState<Record<string, boolean>>({});
  const [heliStandbyOverrides, setHeliStandbyOverrides] = useState<Record<string, number>>({});
  const [heliMinHrsOverrides, setHeliMinHrsOverrides] = useState<Record<string, number>>({});
  const [heliSpeedOverrides, setHeliSpeedOverrides] = useState<Record<string, number>>({});
  const [vesselSpeedOverrides, setVesselSpeedOverrides] = useState<Record<string, number>>({});
  const [aircatSpeedOverride, setAircatSpeedOverride] = useState<number | null>(null);
  const [selectedIdxs, setSelectedIdxs] = useState<number[]>([0]);
  const [reportOpen, setReportOpen] = useState(false);
  const [fuelUnit, setFuelUnit] = useState<FuelUnit>("metric");
  const [fieldSelectorOpen, setFieldSelectorOpen] = useState(false);

  // ── Editable scenario inputs (defaults per user spec) ──
  const [distanceNm, setDistanceNm] = useState(120);
  const [totalPax, setTotalPax] = useState(28);
  const [aircatDayRate, setAircatDayRate] = useState(9500);
  const [jetFuelPerGal, setJetFuelPerGal] = useState(4.50);
  const [mgoPriceMT, setMgoPriceMT] = useState(650);
  const opsDays = 330;

  // Per-field pax overrides (block index → custom pax). Falls back to global totalPax.
  const [fieldPaxOverrides, setFieldPaxOverrides] = useState<Record<number, number>>({});
  const setFieldPax = useCallback((idx: number, pax: number) => {
    setFieldPaxOverrides(prev => ({ ...prev, [idx]: Math.max(1, Math.round(pax)) }));
  }, []);

  // Toggle a field in multi-select
  const toggleField = useCallback((idx: number) => {
    setSelectedIdxs(prev => {
      if (prev.includes(idx)) {
        if (prev.length <= 1) return prev;
        return prev.filter(i => i !== idx);
      }
      return [...prev, idx];
    });
  }, []);

  // Live fuel pricing — update defaults if live data available
  const [livePrices, setLivePrices] = useState<{ jet: number; mgo: number }>(FALLBACK);
  const [priceSource, setPriceSource] = useState<string>("fallback");

  useEffect(() => {
    let cancelled = false;
    supabase.functions.invoke('fuel-prices').then(({ data, error }) => {
      if (cancelled || error || !data) return;
      const jetMT = data.jet?.priceMT;
      const jetGal = data.jet?.pricePerGal;   // native $/gal from FRED
      const mgoMT = data.mgo?.priceMT;
      if (jetMT || mgoMT) {
        setLivePrices({
          jet: jetMT ?? FALLBACK.jet,
          mgo: mgoMT ?? FALLBACK.mgo,
        });
        if (mgoMT) setMgoPriceMT(mgoMT);
        // Prefer native $/gal from FRED to avoid double-conversion rounding
        if (jetGal && jetGal > 0) {
          setJetFuelPerGal(+jetGal.toFixed(2));
        } else if (jetMT) {
          const galPrice = jetPerGal(jetMT);
          if (galPrice > 0) setJetFuelPerGal(galPrice);
        }
        setPriceSource("live");
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const prices = livePrices;
  const primaryHeliKey = heliKeys[0] || "AW139";
  const heli = HELIS[primaryHeliKey];

  // Build per-field results using primary helicopter for aggregate
  const agg = useMemo(() => {
    const fieldResults: FieldResult[] = selectedIdxs.map(idx => {
      const block = BLOCKS[idx];
      const fieldDistance = block.nm;
      const fieldPax = fieldPaxOverrides[idx] ?? totalPax;
      const fieldHeliKey = primaryHeliKey;
      const fieldHeliHourlyRate = HELIS[fieldHeliKey].hourlyRate;
      const fieldAircatDayRate = block.defaultAircatDayRate || aircatDayRate;
      const r = calcAll(fieldHeliKey, fieldDistance, fieldPax, fieldHeliHourlyRate, fieldAircatDayRate, jetFuelPerGal, mgoPriceMT, opsDays, heliStandbyOverrides[fieldHeliKey], heliMinHrsOverrides[fieldHeliKey], heliSpeedOverrides[fieldHeliKey], aircatSpeedOverride ?? undefined);
      return { block, blockIdx: idx, r, pax: fieldPax, distance: fieldDistance, heliHourlyRate: fieldHeliHourlyRate, aircatDayRate: fieldAircatDayRate, heliKey: fieldHeliKey };
    });
    return calcAggregate(fieldResults, opsDays);
  }, [selectedIdxs, primaryHeliKey, totalPax, fieldPaxOverrides, aircatDayRate, jetFuelPerGal, mgoPriceMT, opsDays, heliStandbyOverrides, heliMinHrsOverrides, heliSpeedOverrides, aircatSpeedOverride]);

  const primaryField = agg.fieldResults[0];
  const r = primaryField?.r ?? calcAll(primaryHeliKey, distanceNm, totalPax, heliRateOverrides[primaryHeliKey] ?? heli.hourlyRate, aircatDayRate, jetFuelPerGal, mgoPriceMT, opsDays, heliStandbyOverrides[primaryHeliKey], heliMinHrsOverrides[primaryHeliKey], heliSpeedOverrides[primaryHeliKey], aircatSpeedOverride ?? undefined);
  const block = primaryField?.block ?? BLOCKS[0];
  const multiField = selectedIdxs.length > 1;

  const priceChartData = useMemo(() => buildPriceChartData(prices), [prices]);
  const spreadData = useMemo(() => buildSpreadData(), []);
  const indexData = useMemo(() => buildIndexData(prices), [prices]);

  // Toggle helicopter in multi-select
  const toggleHeli = useCallback((key: string) => {
    setHeliKeys(prev => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        return prev.filter(k => k !== key);
      }
      return [...prev, key];
    });
  }, []);

  // Toggle a competitor vessel in multi-select
  const toggleVessel = useCallback((key: string) => {
    setVesselKeys(prev => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        return prev.filter(k => k !== key);
      }
      return [...prev, key];
    });
  }, []);

  // Compute per-heli results across all selected fields
  const allHeliResults = useMemo(() => {
    return heliKeys.map(hk => {
      const h = HELIS[hk];
      const effectiveRate = heliRateOverrides[hk] ?? h.hourlyRate;
      const fieldCalcs = selectedIdxs.map(idx => {
        const b = BLOCKS[idx];
        const pax = fieldPaxOverrides[idx] ?? totalPax;
        const adRate = b.defaultAircatDayRate || aircatDayRate;
        return calcAll(hk, b.nm, pax, effectiveRate, adRate, jetFuelPerGal, mgoPriceMT, opsDays, heliStandbyOverrides[hk], heliMinHrsOverrides[hk], heliSpeedOverrides[hk], aircatSpeedOverride ?? undefined);
      });
      const avgCostPerPax = fieldCalcs.reduce((s, c) => s + c.heliCostPerPax, 0) / fieldCalcs.length;
      const totalDaily = fieldCalcs.reduce((s, c) => s + c.heliDailyTotal, 0);
      const totalAnnual = totalDaily * opsDays;
      return { key: hk, heli: h, avgCostPerPax, totalDaily, totalAnnual, fieldCalcs };
    });
  }, [heliKeys, selectedIdxs, fieldPaxOverrides, totalPax, aircatDayRate, jetFuelPerGal, mgoPriceMT, opsDays, heliRateOverrides, heliStandbyOverrides, heliMinHrsOverrides, heliSpeedOverrides, aircatSpeedOverride]);

  // Compute selected competitor vessel results across fields
  const selectedVesselResults = useMemo(() => {
    return vesselKeys.map(vk => {
      const v = COMPETITOR_VESSELS[vk];
      const customRate = vesselDayRateOverrides[vk];
      const fieldCalcs = selectedIdxs.map(idx => {
        const b = BLOCKS[idx];
        const pax = fieldPaxOverrides[idx] ?? totalPax;
        return calcCompetitorVessel(vk, b.nm, pax, mgoPriceMT, customRate, vesselSpeedOverrides[vk]);
      });
      const avgCostPerPax = fieldCalcs.reduce((s, c) => s + c.costPerPax, 0) / fieldCalcs.length;
      const totalDaily = fieldCalcs.reduce((s, c) => s + c.dailyTotal, 0);
      const totalAnnual = totalDaily * opsDays;
      return { key: vk, vessel: v, avgCostPerPax, totalDaily, totalAnnual, fieldCalcs };
    });
  }, [vesselKeys, selectedIdxs, fieldPaxOverrides, totalPax, mgoPriceMT, opsDays, vesselDayRateOverrides, vesselSpeedOverrides]);

  // Find lowest cost for chart coloring
  const allCostsPerPax = [
    ...allHeliResults.map(hr => ({ name: hr.heli.name, cost: hr.avgCostPerPax })),
    { name: "AIRCAT 35", cost: agg.avgAircatCostPerPax },
    ...selectedVesselResults.map(svr => ({ name: svr.vessel.name, cost: svr.avgCostPerPax })),
  ];
  const lowestCost = Math.min(...allCostsPerPax.map(c => c.cost));

  const VO_AMBER = "#D97706";
  const heliColors = [VO_RED, "#9333EA", "#B91C1C", "#7C2D12", "#4C1D95", "#831843", "#DC2626"];
  const vesselColors = [VO_BLUE, VO_AMBER, "#7C3AED", "#0891B2", "#DC2626", "#059669"];

  const paxCompData = [
    ...allHeliResults.map((hr, i) => ({
      name: hr.heli.name, cost: parseFloat(hr.avgCostPerPax.toFixed(2)),
      fill: hr.avgCostPerPax <= lowestCost ? VO_GREEN : (heliColors[i % heliColors.length]),
    })),
    { name: "AIRCAT 35", cost: parseFloat(agg.avgAircatCostPerPax.toFixed(2)), fill: agg.avgAircatCostPerPax <= lowestCost ? VO_GREEN : VO_GREEN },
    ...selectedVesselResults.map((svr, i) => ({
      name: svr.vessel.name, cost: parseFloat(svr.avgCostPerPax.toFixed(2)),
      fill: svr.avgCostPerPax <= lowestCost ? VO_GREEN : (vesselColors[i % vesselColors.length]),
    })),
  ];
  const annualCompData = [
    ...allHeliResults.map((hr, i) => ({
      name: hr.heli.name, cost: Math.round(hr.totalAnnual),
      fill: hr.avgCostPerPax <= lowestCost ? VO_GREEN : (heliColors[i % heliColors.length]),
    })),
    { name: "AIRCAT 35", cost: Math.round(agg.totalAircatAnnual), fill: agg.avgAircatCostPerPax <= lowestCost ? VO_GREEN : VO_GREEN },
    ...selectedVesselResults.map((svr, i) => ({
      name: svr.vessel.name, cost: Math.round(svr.totalAnnual),
      fill: svr.avgCostPerPax <= lowestCost ? VO_GREEN : (vesselColors[i % vesselColors.length]),
    })),
  ];

  const tabs = [
    { id: "prices" as const, label: "Fuel Prices", icon: Fuel },
    { id: "analysis" as const, label: "Crew Transfer Comparator", icon: Ship },
    ...(!hideWeatherTab ? [{ id: "weather" as const, label: "Weather Operability", icon: Cloud }] : []),
    ...(!hideReportTab ? [{ id: "report" as const, label: "Generate Report", icon: FileText }] : []),
  ];

  if (!accessGranted) {
    return (
      <Layout>
        <PageHero
          tag="Crew Transfer Intelligence"
          title={<>Fuel cost analysis, <span className="accent-text">live.</span></>}
          subtitle="Compare helicopter vs CTV crew transfer costs with live fuel pricing, configurable scenarios, and branded prospect reports — powered by Velocity Offshore."
          backgroundImage={operationsBg}
        />
        <Section>
          <div className="max-w-md mx-auto">
            <div className="bg-card rounded-2xl border border-border p-8 shadow-lg">
              <h2 className="font-display text-2xl text-foreground mb-2">Access the Analysis Tool</h2>
              <p className="text-muted-foreground text-sm mb-6">Please provide your details below to access the Crew Transfer Intelligence tool.</p>
              <form onSubmit={handleGateSubmit} className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">First Name *</label>
                  <input
                    type="text"
                    value={gateForm.firstName}
                    onChange={e => setGateForm(prev => ({ ...prev, firstName: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="First name"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Last Name *</label>
                  <input
                    type="text"
                    value={gateForm.lastName}
                    onChange={e => setGateForm(prev => ({ ...prev, lastName: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Last name"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Email Address *</label>
                  <input
                    type="email"
                    value={gateForm.email}
                    onChange={e => setGateForm(prev => ({ ...prev, email: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="email@company.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Company *</label>
                  <input
                    type="text"
                    value={gateForm.company}
                    onChange={e => setGateForm(prev => ({ ...prev, company: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Company name"
                    required
                  />
                </div>
                {gateError && <p className="text-sm text-destructive">{gateError}</p>}
                <button
                  type="submit"
                  disabled={gateSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-navy-deep text-primary-foreground text-sm font-medium hover:bg-navy-light transition-colors disabled:opacity-50"
                >
                  {gateSubmitting ? "Submitting…" : "Access Analysis Tool"}
                </button>
              </form>
              <p className="text-[11px] text-muted-foreground mt-4 text-center">Your information will be shared with Velocity Offshore for access purposes only.</p>
            </div>
          </div>
        </Section>
      </Layout>
    );
  }

  const ctiContent = (
    <>
      <Section>
        {/* Tabs + fuel unit toggle */}
        <div className="flex items-center justify-between border-b-2 border-border mb-10">
          <div className="flex">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-[2px] transition-all ${
                    activeTab === tab.id
                      ? "border-secondary text-secondary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 pb-1">
            <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">Units</span>
            <div className="flex rounded-lg border border-border overflow-hidden text-xs">
              <button
                onClick={() => setFuelUnit("metric")}
                className={`px-3 py-1.5 font-medium transition-colors ${fuelUnit === "metric" ? "bg-secondary text-secondary-foreground" : "bg-card text-muted-foreground hover:text-foreground"}`}
              >kg / MT</button>
              <button
                onClick={() => setFuelUnit("imperial")}
                className={`px-3 py-1.5 font-medium transition-colors ${fuelUnit === "imperial" ? "bg-secondary text-secondary-foreground" : "bg-card text-muted-foreground hover:text-foreground"}`}
              >US gal</button>
            </div>
          </div>
        </div>

        {/* TAB: FUEL PRICES */}
        {activeTab === "prices" && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MetricCard label="Jet A-1 (Helicopter Fuel)" value={fuelUnit === "metric" ? prices.jet : jetPerGal(prices.jet)} unit={fuelUnit === "metric" ? "MT" : "US gal"} color={VO_RED} change={fuelUnit === "metric" ? prices.jet - 835 : jetPerGal(prices.jet) - jetPerGal(835)} src={priceSource === "live" ? "FRED DJFUELUSGULF — live spot" : "Indicative market price"} />
              <MetricCard label="MGO (AIRCAT Fuel)" value={fuelUnit === "metric" ? prices.mgo : mgoPerGal(prices.mgo)} unit={fuelUnit === "metric" ? "MT" : "US gal"} color={VO_GREEN} change={fuelUnit === "metric" ? prices.mgo - 612 : mgoPerGal(prices.mgo) - mgoPerGal(612)} src={priceSource === "live" ? "OilPriceAPI — live port spot" : "Indicative market price"} />
            </div>

            {/* Price history chart */}
            <FadeIn>
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground mb-4">27-month price history + 9-month forward projection ($/MT)</div>
                <div className="flex flex-wrap gap-5 mb-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: VO_RED }} /> Jet A-1 (Helicopter)</span>
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: VO_GREEN }} /> MGO (AIRCAT)</span>
                  <span className="flex items-center gap-2"><span className="w-4 border-t-2 border-dashed border-muted-foreground" /> Projection</span>
                </div>
                <ResponsiveContainer width="100%" height={290}>
                  <LineChart data={priceChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" interval="preserveStartEnd" />
                    <YAxis domain={[400, 1000]} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${fmt(v)}`} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Line type="monotone" dataKey="jet" stroke={VO_RED} strokeWidth={2} dot={false} name="Jet A-1 (Helicopter)" />
                    <Line type="monotone" dataKey="mgo" stroke={VO_GREEN} strokeWidth={2} dot={false} name="MGO (AIRCAT)" />
                    <Line type="monotone" dataKey="jetProj" stroke={VO_RED} strokeWidth={2} strokeDasharray="5 4" dot={false} name="Jet A-1 (proj)" />
                    <Line type="monotone" dataKey="mgoProj" stroke={VO_GREEN} strokeWidth={2} strokeDasharray="5 4" dot={false} name="MGO (proj)" />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-[11px] text-muted-foreground mt-3 italic">Projections based on FRED/OilPriceAPI spot data, Platts consensus, OPEC+ trajectory & seasonal demand factors. Not financial advice.</p>
              </div>
            </FadeIn>

            {/* Spread + Index */}
            <div className="grid md:grid-cols-2 gap-4">
              <FadeIn delay={0.1}>
                <div className="bg-card rounded-xl border border-border p-6">
                  <div className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground mb-4">Jet A-1 premium over MGO — AIRCAT fuel advantage ($/MT)</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={spreadData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${fmt(v)}`} domain={[0, 'auto']} />
                      <Tooltip
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                        formatter={(v: number, name: string, props: any) => {
                          const entry = props.payload;
                          return [`$${fmt(v)}/MT (${entry.pctPremium}% premium) — Jet: $${fmt(entry.jetPrice)} vs MGO: $${fmt(entry.mgoPrice)}`, "Aviation Fuel Premium"];
                        }}
                      />
                      <Area type="monotone" dataKey="spread" stroke={VO_GREEN} fill={VO_GREEN} fillOpacity={0.12} strokeWidth={2} name="Spread" />
                    </AreaChart>
                  </ResponsiveContainer>
                  <p className="text-[10px] text-muted-foreground mt-2 italic">The shaded area represents the per-MT cost premium of helicopter fuel (Jet A-1) over AIRCAT fuel (MGO).</p>
                </div>
              </FadeIn>
              <FadeIn delay={0.15}>
                <div className="bg-card rounded-xl border border-border p-6">
                  <div className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground mb-4">Fuel type cost index (Jet A-1 = 100)</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={indexData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="fuel" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis domain={[0, 120]} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v: number) => [`Index: ${v} (Jet=100)`, "Index"]} />
                      <Bar dataKey="index" radius={[6, 6, 0, 0]}>
                        {indexData.map((entry, i) => (
                          <Bar key={i} dataKey="index" fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </FadeIn>
            </div>
          </div>
        )}

        {/* TAB: CREW TRANSFER COMPARATOR */}
        {activeTab === "analysis" && (
          <div className="space-y-6">
            {/* Config — block selector + inputs */}
            <FadeIn>
              <div className="bg-card rounded-xl border border-border p-6">
                <h3 className="font-display text-base font-semibold text-foreground mb-4">Configure your scenario</h3>
                
                {/* Row 1: Helicopter + Vessel + Operating Area */}
                {/* Row 1: Helicopter(s) + Vessel(s) + Operating Area(s) */}
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  {/* Helicopter multi-select */}
                  <div className="relative">
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Select helicopter type(s)</label>
                    <button
                      type="button"
                      onClick={() => setHeliSelectorOpen(!heliSelectorOpen)}
                      className="w-full px-3 py-2.5 border border-border rounded-lg text-sm bg-card text-foreground focus:border-secondary focus:outline-none text-left flex items-center justify-between"
                    >
                      <span className="truncate">
                        {heliKeys.length === 1
                          ? HELIS[heliKeys[0]].name
                          : `${heliKeys.length} helicopters selected`
                        }
                      </span>
                      <span className="text-muted-foreground ml-2">{heliSelectorOpen ? "▲" : "▼"}</span>
                    </button>
                    {heliSelectorOpen && (
                      <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-xl max-h-72 overflow-y-auto">
                        <div className="p-2 border-b border-border flex items-center justify-between">
                          <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">Select one or more helicopters</span>
                          <button onClick={() => setHeliSelectorOpen(false)} className="text-xs text-muted-foreground hover:text-foreground px-2">Done ✓</button>
                        </div>
                        {Object.entries(HELIS).map(([k, h]) => {
                          const checked = heliKeys.includes(k);
                          return (
                            <label
                              key={k}
                              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-accent/10 text-sm ${checked ? "bg-accent/5 font-medium" : ""}`}
                            >
                              <input type="checkbox" checked={checked} onChange={() => toggleHeli(k)} className="rounded border-border text-secondary focus:ring-secondary" />
                              <span className="truncate">{h.name} — {h.pax} pax · ${fmt(h.hourlyRate)}/hr</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Helicopter Contract Parameters Editor */}
                  {heliKeys.length > 0 && (
                    <div className="col-span-full">
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">Helicopter Contract Parameters — enter your contract rates for accurate comparison</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {heliKeys.map(k => {
                          const h = HELIS[k];
                          const effectiveRate = heliRateOverrides[k] ?? h.hourlyRate;
                          const effectiveStandby = heliStandbyOverrides[k] ?? h.standbyFee;
                          const effectiveMinHrs = heliMinHrsOverrides[k] ?? h.minDailyHrs;
                          const effectiveSpeed = heliSpeedOverrides[k] ?? h.kts;
                          const isEditing = heliRateEditing[k];
                          const isCustom = heliRateOverrides[k] !== undefined && heliRateOverrides[k] !== h.hourlyRate;
                          const isStandbyCustom = heliStandbyOverrides[k] !== undefined;
                          const isMinHrsCustom = heliMinHrsOverrides[k] !== undefined;
                          const isSpeedCustom = heliSpeedOverrides[k] !== undefined;
                          const anyCustom = isCustom || isStandbyCustom || isMinHrsCustom || isSpeedCustom;
                          return (
                            <div key={k} className={`rounded-lg border p-3 ${anyCustom ? 'border-secondary bg-secondary/5' : 'border-border bg-card'}`}>
                              <div className="text-xs font-semibold text-foreground mb-0.5 truncate">🚁 {h.name}</div>
                              <div className="text-[10px] text-muted-foreground mb-2">{h.pax} pax · {effectiveSpeed} kts{isSpeedCustom ? ' (custom)' : ''}</div>
                              {/* Hourly Rate */}
                              <div className="mb-2">
                                <div className="text-[10px] text-muted-foreground mb-1 font-medium">Flight Hour Rate</div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm text-muted-foreground font-mono">$</span>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={isEditing ? (heliRateDrafts[k] ?? '') : fmt(effectiveRate)}
                                    onFocus={() => {
                                      setHeliRateEditing(prev => ({ ...prev, [k]: true }));
                                      setHeliRateDrafts(prev => ({ ...prev, [k]: String(effectiveRate) }));
                                    }}
                                    onChange={(e) => {
                                      const raw = e.target.value.replace(/[^0-9.]/g, '');
                                      setHeliRateDrafts(prev => ({ ...prev, [k]: raw }));
                                    }}
                                    onBlur={() => {
                                      const val = parseFloat(heliRateDrafts[k] ?? '');
                                      if (!isNaN(val) && val > 0) {
                                        setHeliRateOverrides(prev => ({ ...prev, [k]: val }));
                                      } else {
                                        setHeliRateOverrides(prev => { const n = { ...prev }; delete n[k]; return n; });
                                      }
                                      setHeliRateEditing(prev => ({ ...prev, [k]: false }));
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                    }}
                                    className="flex-1 px-2.5 py-1.5 border border-border rounded-md text-sm bg-background text-foreground font-mono text-right focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary/30"
                                  />
                                  <span className="text-[10px] text-muted-foreground font-mono">/hr</span>
                                  {isCustom && (
                                    <button
                                      onClick={() => {
                                        setHeliRateOverrides(prev => { const n = { ...prev }; delete n[k]; return n; });
                                        setHeliRateDrafts(prev => { const n = { ...prev }; delete n[k]; return n; });
                                      }}
                                      className="text-[10px] text-muted-foreground hover:text-foreground px-1 py-0.5 rounded hover:bg-accent/20 transition-colors"
                                      title={`Reset to default $${fmt(h.hourlyRate)}`}
                                    >↻</button>
                                  )}
                                </div>
                              </div>
                              {/* Standby Fee */}
                              <div className="mb-2">
                                <div className="text-[10px] text-muted-foreground mb-1 font-medium">Daily Standby / Availability Fee</div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm text-muted-foreground font-mono">$</span>
                                  <input
                                    type="number"
                                    value={effectiveStandby}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value);
                                      if (!isNaN(val) && val >= 0) {
                                        setHeliStandbyOverrides(prev => ({ ...prev, [k]: val }));
                                      }
                                    }}
                                    className="flex-1 px-2.5 py-1.5 border border-border rounded-md text-sm bg-background text-foreground font-mono text-right focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary/30"
                                  />
                                  <span className="text-[10px] text-muted-foreground font-mono">/day</span>
                                  {isStandbyCustom && (
                                    <button
                                      onClick={() => setHeliStandbyOverrides(prev => { const n = { ...prev }; delete n[k]; return n; })}
                                      className="text-[10px] text-muted-foreground hover:text-foreground px-1 py-0.5 rounded hover:bg-accent/20 transition-colors"
                                      title={`Reset to default $${fmt(h.standbyFee)}`}
                                    >↻</button>
                                  )}
                                </div>
                              </div>
                              {/* Minimum Daily Hours */}
                              <div>
                                <div className="text-[10px] text-muted-foreground mb-1 font-medium">Min. Guaranteed Flight Hours/Day</div>
                                <div className="flex items-center gap-1.5">
                                  <input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    max="24"
                                    value={effectiveMinHrs}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value);
                                      if (!isNaN(val) && val >= 0) {
                                        setHeliMinHrsOverrides(prev => ({ ...prev, [k]: val }));
                                      }
                                    }}
                                    className="flex-1 px-2.5 py-1.5 border border-border rounded-md text-sm bg-background text-foreground font-mono text-right focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary/30"
                                  />
                                  <span className="text-[10px] text-muted-foreground font-mono">hrs</span>
                                  {isMinHrsCustom && (
                                    <button
                                      onClick={() => setHeliMinHrsOverrides(prev => { const n = { ...prev }; delete n[k]; return n; })}
                                      className="text-[10px] text-muted-foreground hover:text-foreground px-1 py-0.5 rounded hover:bg-accent/20 transition-colors"
                                      title={`Reset to default ${h.minDailyHrs} hrs`}
                                    >↻</button>
                                  )}
                                </div>
                              </div>
                              {/* Cruise Speed */}
                              <div className="mt-2">
                                <div className="text-[10px] text-muted-foreground mb-1 font-medium">Cruise Speed</div>
                                <div className="flex items-center gap-1.5">
                                  <input
                                    type="number"
                                    step="1"
                                    min="50"
                                    max="300"
                                    value={effectiveSpeed}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value);
                                      if (!isNaN(val) && val > 0) {
                                        setHeliSpeedOverrides(prev => ({ ...prev, [k]: val }));
                                      }
                                    }}
                                    className="flex-1 px-2.5 py-1.5 border border-border rounded-md text-sm bg-background text-foreground font-mono text-right focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary/30"
                                  />
                                  <span className="text-[10px] text-muted-foreground font-mono">kts</span>
                                  {isSpeedCustom && (
                                    <button
                                      onClick={() => setHeliSpeedOverrides(prev => { const n = { ...prev }; delete n[k]; return n; })}
                                      className="text-[10px] text-muted-foreground hover:text-foreground px-1 py-0.5 rounded hover:bg-accent/20 transition-colors"
                                      title={`Reset to default ${h.kts} kts`}
                                    >↻</button>
                                  )}
                                </div>
                              </div>
                              {anyCustom && (
                                <div className="text-[10px] text-secondary mt-1.5 font-medium">Custom contract parameters applied</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Competitor vessel multi-select */}
                  <div className="relative">
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Select competitor vessel(s)</label>
                    <button
                      type="button"
                      onClick={() => setVesselSelectorOpen(!vesselSelectorOpen)}
                      className="w-full px-3 py-2.5 border border-border rounded-lg text-sm bg-card text-foreground focus:border-secondary focus:outline-none text-left flex items-center justify-between"
                    >
                      <span className="truncate">
                        {vesselKeys.length === 1
                          ? COMPETITOR_VESSELS[vesselKeys[0]].name
                          : `${vesselKeys.length} vessels selected`
                        }
                      </span>
                      <span className="text-muted-foreground ml-2">{vesselSelectorOpen ? "▲" : "▼"}</span>
                    </button>
                    {vesselSelectorOpen && (
                      <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-xl max-h-72 overflow-y-auto">
                        <div className="p-2 border-b border-border flex items-center justify-between">
                          <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">Select one or more vessels</span>
                          <button onClick={() => setVesselSelectorOpen(false)} className="text-xs text-muted-foreground hover:text-foreground px-2">Done ✓</button>
                        </div>
                        {Object.entries(COMPETITOR_VESSELS).map(([k, v]) => {
                          const checked = vesselKeys.includes(k);
                          return (
                            <div key={k} className={`px-3 py-2 hover:bg-accent/10 ${checked ? "bg-accent/5" : ""}`}>
                              <label className="flex items-center gap-2.5 cursor-pointer text-sm">
                                <input type="checkbox" checked={checked} onChange={() => toggleVessel(k)} className="rounded border-border text-secondary focus:ring-secondary" />
                                <span className="truncate flex-1">{v.name} — {v.pax} pax · {v.kts} kts</span>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Competitor Day Rate Editor — shown when vessels are selected */}
                  {vesselKeys.length > 0 && (
                    <div className="col-span-full">
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">Competitor Vessel Parameters — enter your contract rates and speeds for accurate comparison</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {vesselKeys.map(k => {
                          const v = COMPETITOR_VESSELS[k];
                          const effectiveRate = vesselDayRateOverrides[k] ?? v.dayRate;
                          const effectiveSpeed = vesselSpeedOverrides[k] ?? v.kts;
                          const isEditing = dayRateEditing[k];
                          const isRateCustom = vesselDayRateOverrides[k] !== undefined && vesselDayRateOverrides[k] !== v.dayRate;
                          const isSpeedCustom = vesselSpeedOverrides[k] !== undefined;
                          const anyCustom = isRateCustom || isSpeedCustom;
                          return (
                            <div key={k} className={`rounded-lg border p-3 ${anyCustom ? 'border-secondary bg-secondary/5' : 'border-border bg-card'}`}>
                              <div className="text-xs font-semibold text-foreground mb-0.5 truncate">{v.name}</div>
                              <div className="text-[10px] text-muted-foreground mb-2">{v.pax} pax · {effectiveSpeed} kts{isSpeedCustom ? ' (custom)' : ''} · {v.dp} · Default: ${fmt(v.dayRate)}/day</div>
                              {/* Day Rate */}
                              <div className="mb-2">
                                <div className="text-[10px] text-muted-foreground mb-1 font-medium">Day Rate</div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm text-muted-foreground font-mono">$</span>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={isEditing ? (dayRateDrafts[k] ?? '') : fmt(effectiveRate)}
                                    onFocus={() => {
                                      setDayRateEditing(prev => ({ ...prev, [k]: true }));
                                      setDayRateDrafts(prev => ({ ...prev, [k]: String(effectiveRate) }));
                                    }}
                                    onChange={(e) => {
                                      const raw = e.target.value.replace(/[^0-9.]/g, '');
                                      setDayRateDrafts(prev => ({ ...prev, [k]: raw }));
                                    }}
                                    onBlur={() => {
                                      const val = parseFloat(dayRateDrafts[k] ?? '');
                                      if (!isNaN(val) && val > 0) {
                                        setVesselDayRateOverrides(prev => ({ ...prev, [k]: val }));
                                      } else {
                                        setVesselDayRateOverrides(prev => { const n = { ...prev }; delete n[k]; return n; });
                                      }
                                      setDayRateEditing(prev => ({ ...prev, [k]: false }));
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                    }}
                                    className="flex-1 px-2.5 py-1.5 border border-border rounded-md text-sm bg-background text-foreground font-mono text-right focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary/30"
                                  />
                                  <span className="text-xs text-muted-foreground font-mono">/day</span>
                                  {isRateCustom && (
                                    <button
                                      onClick={() => {
                                        setVesselDayRateOverrides(prev => { const n = { ...prev }; delete n[k]; return n; });
                                        setDayRateDrafts(prev => { const n = { ...prev }; delete n[k]; return n; });
                                      }}
                                      className="text-xs text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded hover:bg-accent/20 transition-colors"
                                      title={`Reset to default $${fmt(v.dayRate)}`}
                                    >↻</button>
                                  )}
                                </div>
                              </div>
                              {/* Speed */}
                              <div>
                                <div className="text-[10px] text-muted-foreground mb-1 font-medium">Cruise Speed</div>
                                <div className="flex items-center gap-1.5">
                                  <input
                                    type="number"
                                    step="1"
                                    min="5"
                                    max="60"
                                    value={effectiveSpeed}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value);
                                      if (!isNaN(val) && val > 0) {
                                        setVesselSpeedOverrides(prev => ({ ...prev, [k]: val }));
                                      }
                                    }}
                                    className="flex-1 px-2.5 py-1.5 border border-border rounded-md text-sm bg-background text-foreground font-mono text-right focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary/30"
                                  />
                                  <span className="text-[10px] text-muted-foreground font-mono">kts</span>
                                  {isSpeedCustom && (
                                    <button
                                      onClick={() => setVesselSpeedOverrides(prev => { const n = { ...prev }; delete n[k]; return n; })}
                                      className="text-[10px] text-muted-foreground hover:text-foreground px-1 py-0.5 rounded hover:bg-accent/20 transition-colors"
                                      title={`Reset to default ${v.kts} kts`}
                                    >↻</button>
                                  )}
                                </div>
                              </div>
                              {anyCustom && (
                                <div className="text-[10px] text-secondary mt-1.5 font-medium">Custom parameters applied</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Field multi-select */}
                  <div className="relative">
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Select field(s) / operating area(s)</label>
                    <button
                      type="button"
                      onClick={() => setFieldSelectorOpen(!fieldSelectorOpen)}
                      className="w-full px-3 py-2.5 border border-border rounded-lg text-sm bg-card text-foreground focus:border-secondary focus:outline-none text-left flex items-center justify-between"
                    >
                      <span className="truncate">
                        {selectedIdxs.length === 1
                          ? BLOCKS[selectedIdxs[0]].name
                          : `${selectedIdxs.length} fields selected`
                        }
                      </span>
                      <span className="text-muted-foreground ml-2">{fieldSelectorOpen ? "▲" : "▼"}</span>
                    </button>
                    {fieldSelectorOpen && (
                      <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-xl max-h-72 overflow-y-auto">
                        <div className="p-2 border-b border-border flex items-center justify-between">
                          <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">Select one or more fields</span>
                          <button onClick={() => setFieldSelectorOpen(false)} className="text-xs text-muted-foreground hover:text-foreground px-2">Done ✓</button>
                        </div>
                        {BLOCKS.map((b, idx) => {
                          const checked = selectedIdxs.includes(idx);
                          const badge = b.status === "development" ? "🟠" : b.status === "pre-fid" ? "⚪" : "";
                          const tag = b.status === "development" ? " [DEV]" : b.status === "pre-fid" ? " [PRE-FID]" : "";
                          return (
                            <label
                              key={idx}
                              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-accent/10 text-sm ${checked ? "bg-accent/5 font-medium" : ""}`}
                            >
                              <input type="checkbox" checked={checked} onChange={() => toggleField(idx)} className="rounded border-border text-secondary focus:ring-secondary" />
                              <span className="truncate">{badge} {b.name} — {b.nm}nm{tag}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 2: Editable parameters */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <InputField label="Distance (nm)" value={distanceNm} onChange={setDistanceNm} suffix="nm" min={10} max={500} step={5} />
                  <InputField label={multiField ? "Default crew pax" : "Daily crew pax"} value={totalPax} onChange={setTotalPax} suffix="pax" min={1} max={300} step={1} />
                  <InputField label="Jet fuel price" value={jetFuelPerGal} onChange={setJetFuelPerGal} prefix="$" suffix="/gal" min={0} step={0.10} />
                  <InputField label="AIRCAT day rate" value={aircatDayRate} onChange={setAircatDayRate} prefix="$" suffix="/day" min={0} step={500} />
                  <InputField label="AIRCAT cruise speed" value={aircatSpeedOverride ?? AIRCAT_SPEED_KTS} onChange={(v) => setAircatSpeedOverride(v === AIRCAT_SPEED_KTS ? null : v)} suffix="kts" min={10} max={60} step={1} />
                  <InputField label="MGO price" value={mgoPriceMT} onChange={setMgoPriceMT} prefix="$" suffix="/MT" min={0} step={10} />
                </div>

                {/* Quick reference */}
                <div className="mt-3 text-[10px] text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                  {heliKeys.map(hk => { const h = HELIS[hk]; const rate = heliRateOverrides[hk] ?? h.hourlyRate; return <span key={hk}>🚁 {h.name}: {h.pax} pax · {h.kts} kts · ${fmt(rate)}/hr{heliRateOverrides[hk] !== undefined ? ' (custom)' : ''}</span>; })}
                  <span>⛵ AIRCAT 35: {AIRCAT_PAX} pax · {AIRCAT_SPEED_KTS} kts · {AIRCAT_MGO_BURN_MT_HR} MT/hr MGO</span>
                  {vesselKeys.map(vk => { const cv = COMPETITOR_VESSELS[vk]; const rate = vesselDayRateOverrides[vk] ?? cv.dayRate; return <span key={vk}>⛵ {cv.name}: {cv.pax} pax · {cv.kts} kts · ${fmt(rate)}/day</span>; })}
                </div>
              </div>
            </FadeIn>

            {/* ── SCENARIO BUILDER — per-field build cards ── */}
            <FadeIn delay={0.05}>
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="px-4 py-3 border-b border-border bg-accent/5 flex items-center justify-between">
                  <span className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground">
                    Scenario Builder — {selectedIdxs.length} field{selectedIdxs.length > 1 ? "s" : ""} · {heliKeys.length} helicopter{heliKeys.length > 1 ? "s" : ""} · {vesselKeys.length} vessel{vesselKeys.length > 1 ? "s" : ""} · AIRCAT 35
                  </span>
                </div>
                <div className="divide-y divide-border/50">
                  {agg.fieldResults.map((fr, i) => {
                    const fieldPax = fieldPaxOverrides[fr.blockIdx] ?? totalPax;
                    return (
                      <div key={i} className="px-4 py-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex items-center gap-2 min-w-[180px]">
                            <span className="text-xs font-semibold text-foreground">{fr.block.fieldName || fr.block.name}</span>
                            <span className="text-[10px] text-muted-foreground font-mono">{fr.distance}nm</span>
                            {selectedIdxs.length > 1 && (
                              <button onClick={() => toggleField(fr.blockIdx)} className="text-muted-foreground hover:text-foreground text-xs">×</button>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-muted-foreground font-medium">Crew:</span>
                            <InputField
                              value={fieldPax}
                              onChange={v => setFieldPax(fr.blockIdx, v)}
                              min={1} max={300} step={1} suffix="pax"
                              className="w-16 px-2 py-1 border border-border rounded text-xs bg-background text-foreground font-mono text-right focus:border-secondary focus:outline-none"
                            />
                          </div>
                          <div className="flex items-center gap-1 flex-wrap">
                            {heliKeys.map((hk, hi) => (
                              <span key={hk} className="text-[9px] font-medium px-1.5 py-0.5 rounded-full border" style={{ borderColor: `${heliColors[hi % heliColors.length]}44`, color: heliColors[hi % heliColors.length], background: `${heliColors[hi % heliColors.length]}08` }}>
                                🚁 {HELIS[hk].name.split(' ').pop()}
                              </span>
                            ))}
                            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-teal/10 text-teal border border-teal/30">⛵ AIRCAT 35</span>
                            {vesselKeys.map((vk, vi) => (
                              <span key={vk} className="text-[9px] font-medium px-1.5 py-0.5 rounded-full border" style={{ borderColor: `${vesselColors[vi % vesselColors.length]}44`, color: vesselColors[vi % vesselColors.length], background: `${vesselColors[vi % vesselColors.length]}08` }}>
                                ⛵ {COMPETITOR_VESSELS[vk].name.split(' ').slice(-1)[0]}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </FadeIn>

            {/* ── PORTFOLIO COMPARISON TABLE (multi-field) ── */}
            {multiField && (
              <FadeIn delay={0.06}>
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="px-4 py-3 border-b border-border bg-accent/5">
                    <span className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground">Portfolio Comparison Table</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground">
                          <th className="text-left px-4 py-2 font-semibold">Field</th>
                          <th className="text-right px-3 py-2 font-semibold">Dist</th>
                          <th className="text-right px-3 py-2 font-semibold">Crew</th>
                          {allHeliResults.map((hr, hi) => (
                            <th key={hr.key} className="text-right px-3 py-2 font-semibold" style={{ color: heliColors[hi % heliColors.length] }}>{hr.heli.name.split(' ').pop()} $/pax</th>
                          ))}
                          <th className="text-right px-3 py-2 font-semibold" style={{ color: VO_GREEN }}>AIRCAT $/pax</th>
                          {selectedVesselResults.map((svr, vi) => (
                            <th key={svr.key} className="text-right px-3 py-2 font-semibold" style={{ color: vesselColors[vi % vesselColors.length] }}>{svr.vessel.name.split(' ').slice(-1)[0]} $/pax</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {agg.fieldResults.map((fr, i) => {
                          const fieldPax = fieldPaxOverrides[fr.blockIdx] ?? totalPax;
                          return (
                            <tr key={i} className="border-b border-border/50 hover:bg-accent/5">
                              <td className="px-4 py-2 font-medium text-foreground">{fr.block.fieldName || fr.block.name}</td>
                              <td className="text-right px-3 py-2 font-mono">{fr.distance}nm</td>
                              <td className="text-right px-3 py-2 font-mono">{fieldPax}</td>
                              {allHeliResults.map((hr, hi) => {
                                const hCalc = calcAll(hr.key, fr.distance, fieldPax, heliRateOverrides[hr.key] ?? hr.heli.hourlyRate, fr.aircatDayRate, jetFuelPerGal, mgoPriceMT, opsDays, heliStandbyOverrides[hr.key], heliMinHrsOverrides[hr.key], heliSpeedOverrides[hr.key], aircatSpeedOverride ?? undefined);
                                return (
                                  <td key={hr.key} className="text-right px-3 py-2 font-mono" style={{ color: heliColors[hi % heliColors.length] }}>${fmtD(hCalc.heliCostPerPax)}</td>
                                );
                              })}
                              <td className="text-right px-3 py-2 font-mono" style={{ color: VO_GREEN }}>${fmtD(fr.r.aircatCostPerPax)}</td>
                              {selectedVesselResults.map((svr, vi) => {
                                const cv = calcCompetitorVessel(svr.key, fr.distance, fieldPax, mgoPriceMT, vesselDayRateOverrides[svr.key], vesselSpeedOverrides[svr.key]);
                                return (
                                  <td key={svr.key} className="text-right px-3 py-2 font-mono" style={{ color: vesselColors[vi % vesselColors.length] }}>${fmtD(cv.costPerPax)}</td>
                                );
                              })}
                            </tr>
                          );
                        })}
                        <tr className="font-bold bg-accent/10">
                          <td className="px-4 py-2.5 text-foreground">PORTFOLIO AVG</td>
                          <td className="text-right px-3 py-2.5">—</td>
                          <td className="text-right px-3 py-2.5 font-mono">{agg.totalPax}</td>
                          {allHeliResults.map((hr, hi) => (
                            <td key={hr.key} className="text-right px-3 py-2.5 font-mono" style={{ color: heliColors[hi % heliColors.length] }}>${fmtD(hr.avgCostPerPax)}</td>
                          ))}
                          <td className="text-right px-3 py-2.5 font-mono" style={{ color: VO_GREEN }}>${fmtD(agg.avgAircatCostPerPax)}</td>
                          {selectedVesselResults.map((svr, vi) => (
                            <td key={svr.key} className="text-right px-3 py-2.5 font-mono" style={{ color: vesselColors[vi % vesselColors.length] }}>${fmtD(svr.avgCostPerPax)}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </FadeIn>
            )}

            {/* Single field detail info (only show for single selection) */}
            {!multiField && (
              <FadeIn delay={0.05}>
                <div className="bg-accent/10 border border-accent/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-display text-sm font-semibold text-foreground">{block.fieldName || block.name}</span>
                    {block.status === "producing" && (
                      <span className="text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">PRODUCING</span>
                    )}
                    {block.status === "development" && (
                      <span className="text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300">DEVELOPMENT</span>
                    )}
                    {block.status === "pre-fid" && (
                      <span className="text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-300">PRE-FID</span>
                    )}
                  </div>
                  {block.blocks && (
                    <div className="text-xs text-muted-foreground mb-2 font-mono">{block.blocks}</div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-accent-foreground/70">
                    <span>📍 Shore base: <strong className="text-foreground">{block.base}</strong></span>
                    <span>🏭 Production unit: <strong className="text-foreground">{block.productionUnit || block.fpso}</strong></span>
                    <span>🏢 Operator: <strong className="text-foreground">{block.operator}</strong></span>
                    <span>📏 Distance: <strong className="text-foreground">{block.nm} nm</strong></span>
                    <span>🌊 Sea state: <strong className="text-foreground">{block.seaDesc}</strong></span>
                    <span>📊 Availability: <strong className="text-foreground">{block.avail}%</strong></span>
                    {block.pob && <span>👥 POB: <strong className="text-foreground">{block.pob}</strong></span>}
                    {block.heliOperator && <span>🚁 Heli operator: <strong className="text-foreground">{block.heliOperator}</strong></span>}
                    {block.heliSeatRate && <span>💺 Heli seat rate: <strong className="text-foreground">${fmt(block.heliSeatRate)}/seat (RT est.)</strong></span>}
                    {block.waterDepth && <span>🔽 Water depth: <strong className="text-foreground">{block.waterDepth}</strong></span>}
                    {block.bpInterest && <span>📊 Operator interest: <strong className="text-foreground">{block.bpInterest}</strong></span>}
                    {block.partners && <span>🤝 Partners: <strong className="text-foreground">{block.partners}</strong></span>}
                    {block.regulatory && <span>⚖️ Regulatory: <strong className="text-foreground">{block.regulatory}</strong></span>}
                    {block.licence && <span>📜 Licence: <strong className="text-foreground">{block.licence}</strong></span>}
                    {block.coords && <span>🧭 Coordinates: <strong className="text-foreground">{block.coords}</strong></span>}
                    {block.discoveryYear && <span>🔍 Discovery: <strong className="text-foreground">{block.discoveryYear}</strong></span>}
                    {block.productionStart && <span>🚀 Production start: <strong className="text-foreground">{block.productionStart}</strong></span>}
                    {block.resourcesInPlace && <span>🛢️ Resources: <strong className="text-foreground">{block.resourcesInPlace}</strong></span>}
                  </div>
                </div>
              </FadeIn>
            )}

            {/* Savings banner — portfolio or single */}
            {(() => {
              const aggWins = agg.avgAircatCostPerPax < agg.avgHeliCostPerPax;
              const aggSavPct = Math.abs(agg.avgPctAdvantage);
              return (
                <FadeIn delay={0.08}>
                  <div className={`border rounded-xl p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 ${aggWins ? "bg-gradient-to-r from-teal/10 to-secondary/10 border-teal/30" : "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300"}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full animate-pulse ${aggWins ? "bg-teal" : "bg-amber-500"}`} />
                      <span className="text-sm font-semibold text-foreground">
                        {multiField
                          ? (aggWins ? `AIRCAT 35 portfolio advantage across ${selectedIdxs.length} fields` : `Helicopter is more cost-efficient across ${selectedIdxs.length} fields`)
                          : (aggWins ? `AIRCAT 35 advantage over ${heli.name}` : `${heli.name} is more cost-efficient in this scenario`)
                        }
                      </span>
                    </div>
                    <span className={`text-base font-bold font-mono ${aggWins ? "text-teal" : "text-amber-700"}`}>
                      {aggWins
                        ? `${aggSavPct}% lower avg cost/pax — saving $${fmtM(Math.abs(agg.totalAnnualSaving) / 1_000_000)}M/year`
                        : `Helicopter is $${fmtD(Math.abs(agg.avgHeliCostPerPax - agg.avgAircatCostPerPax))}/pax cheaper (${aggSavPct}%)`
                      }
                    </span>
                  </div>
                </FadeIn>
              );
            })()}

            {/* Result summary cards */}
            {(() => {
              const aggWins = agg.avgAircatCostPerPax < agg.avgHeliCostPerPax;
              const aggSavPct = Math.abs(agg.avgPctAdvantage);
              return (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <FadeIn delay={0.1}>
                      <div className="rounded-xl p-4 bg-muted">
                        <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-2">{heli.name} — daily{multiField ? " (all)" : ""}</div>
                        <div className="text-xl font-bold font-mono" style={{ color: VO_RED }}>${fmt(Math.round(agg.totalHeliDaily))}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{multiField ? `Sum of ${selectedIdxs.length} fields` : "Charter + fuel + maint"}</div>
                      </div>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                      <div className={`rounded-xl p-4 ${aggWins ? "bg-teal/5 border border-teal/20" : "bg-muted"}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">AIRCAT 35 — daily{multiField ? " (all)" : ""}</div>
                          {aggWins && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-teal/15 text-teal">{aggSavPct}%</span>}
                        </div>
                        <div className="text-xl font-bold font-mono" style={{ color: VO_GREEN }}>${fmt(Math.round(agg.totalAircatDaily))}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{multiField ? `Sum of ${selectedIdxs.length} fields` : "Day rate + fuel + maint"}</div>
                      </div>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                      <div className="rounded-xl p-4 bg-muted">
                        <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-2">{heli.name} — avg $/pax</div>
                        <div className="text-xl font-bold font-mono" style={{ color: VO_RED }}>${fmtD(agg.avgHeliCostPerPax)}</div>
                      </div>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                      <div className={`rounded-xl p-4 ${aggWins ? "bg-teal/5 border border-teal/20" : "bg-muted"}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">AIRCAT 35 — avg $/pax</div>
                          {aggWins && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-teal/15 text-teal">{aggSavPct}% saving</span>}
                        </div>
                        <div className="text-xl font-bold font-mono" style={{ color: VO_GREEN }}>${fmtD(agg.avgAircatCostPerPax)}</div>
                      </div>
                    </FadeIn>
                  </div>
                  {/* Additional helicopter + vessel summary cards */}
                  {(allHeliResults.length > 1 || selectedVesselResults.length > 0) && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-3">
                      {allHeliResults.slice(1).map((hr, hi) => (
                        <FadeIn key={hr.key} delay={0.12 + hi * 0.03}>
                          <div className="rounded-xl p-4 bg-muted border" style={{ borderColor: `${heliColors[(hi + 1) % heliColors.length]}33` }}>
                            <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-2">{hr.heli.name} — avg $/pax</div>
                            <div className="text-xl font-bold font-mono" style={{ color: heliColors[(hi + 1) % heliColors.length] }}>${fmtD(hr.avgCostPerPax)}</div>
                            <div className="text-[10px] text-muted-foreground mt-1">${fmt(Math.round(hr.totalDaily))}/day · ${fmtM(hr.totalAnnual / 1_000_000)}M/yr</div>
                          </div>
                        </FadeIn>
                      ))}
                      {selectedVesselResults.map((svr, vi) => (
                        <FadeIn key={svr.key} delay={0.12 + vi * 0.03}>
                          <div className="rounded-xl p-4 bg-muted border" style={{ borderColor: `${vesselColors[vi % vesselColors.length]}33` }}>
                            <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-2">{svr.vessel.name} — avg $/pax</div>
                            <div className="text-xl font-bold font-mono" style={{ color: vesselColors[vi % vesselColors.length] }}>${fmtD(svr.avgCostPerPax)}</div>
                            <div className="text-[10px] text-muted-foreground mt-1">${fmt(Math.round(svr.totalDaily))}/day · ${fmtM(svr.totalAnnual / 1_000_000)}M/yr</div>
                          </div>
                        </FadeIn>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Advantage summary */}
            {(() => {
              const aggWins = agg.avgAircatCostPerPax < agg.avgHeliCostPerPax;
              const aggSavPct = Math.abs(agg.avgPctAdvantage);
              return (
                <FadeIn delay={0.12}>
                  <div className={`border rounded-xl p-5 ${aggWins ? "bg-gradient-to-r from-teal/5 to-teal/15 border-teal/30" : "bg-gradient-to-r from-amber-50/50 to-orange-50/50 border-amber-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Leaf className={`w-4 h-4 ${aggWins ? "text-teal" : "text-amber-600"}`} />
                      <span className={`text-xs font-bold tracking-widest uppercase ${aggWins ? "text-teal/80" : "text-amber-700/80"}`}>
                        {aggWins ? (multiField ? "PORTFOLIO AIRCAT 35 ADVANTAGE" : "AIRCAT 35 delivers") : "Honest comparison"}
                      </span>
                    </div>
                    <div className={`text-2xl md:text-3xl font-extrabold font-mono ${aggWins ? "text-teal" : "text-amber-700"}`}>
                      {aggWins
                        ? `${aggSavPct}% lower cost — saving $${fmtM(Math.abs(agg.totalAnnualSaving) / 1_000_000)}M/year${multiField ? " (portfolio)" : ""}`
                        : `Helicopter is ${aggSavPct}% cheaper per pax${multiField ? " on average" : " in this scenario"}`
                      }
                    </div>
                    <div className={`text-xs mt-2 ${aggWins ? "text-teal/70" : "text-amber-700/70"}`}>
                      AIRCAT 35: ${fmtD(agg.avgAircatCostPerPax)}/pax
                      {allHeliResults.map(hr => ` · ${hr.heli.name}: $${fmtD(hr.avgCostPerPax)}/pax`).join('')}
                      {selectedVesselResults.map(svr => ` · ${svr.vessel.name}: $${fmtD(svr.avgCostPerPax)}/pax`).join('')}
                      {" — "}Annual: ${fmtM(agg.totalAircatAnnual / 1_000_000)}M (AIRCAT)
                      {!aggWins && " — AIRCAT 35 remains advantageous for capacity, multi-FPSO routing, and helideck pressure relief"}
                    </div>
                    <div className="text-[10px] text-muted-foreground/60 mt-3 italic">
                      Note: The ✓ RECOMMENDED badge is applied to the transport mode with the lowest cost per passenger based on your configured inputs. Adjust day rates, hourly rates, distance, and pax count to see how recommendations shift.
                    </div>
                  </div>
                </FadeIn>
              );
            })()}

            {/* Vehicle detail cards — per-field */}
            {(multiField ? agg.fieldResults : [{ block, blockIdx: selectedIdxs[0], r, pax: totalPax, distance: distanceNm, heliHourlyRate: heli.hourlyRate, aircatDayRate, heliKey: primaryHeliKey } as FieldResult]).map((fr, fi) => {
              const fieldPax = fieldPaxOverrides[fr.blockIdx] ?? totalPax;
              const fR = fr.r;
              const fWins = fR.aircatCostPerPax < fR.heliCostPerPax;
              return (
                <div key={fi}>
                  {multiField && (
                    <div className="mt-6 mb-2 flex items-center gap-2">
                      <span className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground">Field {fi + 1}:</span>
                      <span className="text-sm font-semibold text-foreground">{fr.block.fieldName || fr.block.name}</span>
                      <span className="text-xs text-muted-foreground">· {fr.distance}nm · {fieldPax} pax</span>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Compute all costs to find cheapest option */}
                    {(() => {
                      // Build all options with their cost/pax
                      const allOptions: { type: 'heli' | 'aircat' | 'vessel'; key: string; costPerPax: number }[] = [];
                      heliKeys.forEach(hk => {
                        const fHeli = HELIS[hk];
                        const heliEffRate = heliRateOverrides[hk] ?? fHeli.hourlyRate;
                        const hCalc = calcAll(hk, fr.distance, fieldPax, heliEffRate, fr.aircatDayRate, jetFuelPerGal, mgoPriceMT, opsDays, heliStandbyOverrides[hk], heliMinHrsOverrides[hk], heliSpeedOverrides[hk], aircatSpeedOverride ?? undefined);
                        allOptions.push({ type: 'heli', key: hk, costPerPax: hCalc.heliCostPerPax });
                      });
                      allOptions.push({ type: 'aircat', key: 'aircat35', costPerPax: fR.aircatCostPerPax });
                      vesselKeys.forEach(vk => {
                        const cv = calcCompetitorVessel(vk, fr.distance, fieldPax, mgoPriceMT, vesselDayRateOverrides[vk], vesselSpeedOverrides[vk]);
                        allOptions.push({ type: 'vessel', key: vk, costPerPax: cv.costPerPax });
                      });
                      const cheapestCostPerPax = Math.min(...allOptions.map(o => o.costPerPax));
                      const cheapest = allOptions.find(o => o.costPerPax === cheapestCostPerPax);

                      const isAircatCheapest = cheapest?.type === 'aircat';
                      const cheapestHeliKey = cheapest?.type === 'heli' ? cheapest.key : null;
                      const cheapestVesselKey = cheapest?.type === 'vessel' ? cheapest.key : null;

                      return (
                        <>
                          {/* Helicopter cards */}
                          {heliKeys.map((hk, hi) => {
                            const fHeli = HELIS[hk];
                            const heliEffRate = heliRateOverrides[hk] ?? fHeli.hourlyRate;
                            const hCalc = calcAll(hk, fr.distance, fieldPax, heliEffRate, fr.aircatDayRate, jetFuelPerGal, mgoPriceMT, opsDays, heliStandbyOverrides[hk], heliMinHrsOverrides[hk], heliSpeedOverrides[hk], aircatSpeedOverride ?? undefined);
                            const isThisCheapest = cheapestHeliKey === hk;
                            return (
                              <FadeIn key={hk} delay={0.15 + fi * 0.05 + hi * 0.03}>
                                <div className={`bg-card rounded-xl p-5 relative overflow-hidden ${isThisCheapest ? 'border-2 border-teal' : 'border border-border'}`} style={!isThisCheapest ? { borderColor: `${heliColors[hi % heliColors.length]}33` } : undefined}>
                                  {isThisCheapest && <div className="absolute top-0 right-0 bg-teal text-white text-[9px] font-bold tracking-widest uppercase px-3 py-1 rounded-bl-lg">✓ RECOMMENDED</div>}
                                  <span className="inline-block text-[11px] px-3 py-1 rounded-md font-semibold mb-3" style={{ background: `${heliColors[hi % heliColors.length]}12`, color: heliColors[hi % heliColors.length] }}>Helicopter{heliKeys.length > 1 ? ` ${hi + 1}` : ""}</span>
                                  <div className="font-display text-sm font-bold text-foreground mb-0.5">{fHeli.name}</div>
                                  <div className="text-[10px] text-muted-foreground mb-3">{fHeli.pax} pax · {fHeli.kts} kts · ${fmt(heliEffRate)}/hr{heliRateOverrides[hk] !== undefined ? ' (custom)' : ''}</div>
                                  <div className="space-y-0">
                                    <SpecRow label="Flights needed" value={`${hCalc.heliFlights} (${fieldPax} ÷ ${fHeli.pax})`} variant={isThisCheapest ? "good" : "warn"} />
                                    <SpecRow label="RT time" value={toHHMM(hCalc.heliRTHrs)} />
                                    <SpecRow label="Total time" value={toHHMM(hCalc.heliRTHrs * hCalc.heliFlights)} />
                                    <SpecRow label="Fuel" value={`${fmt(Math.round(hCalc.heliTotalFuelGal))} gal Jet A-1 · $${fmt(Math.round(hCalc.heliTotalFuelCost))}`} variant={isThisCheapest ? "good" : "warn"} />
                                    <SpecRow label="Charter" value={`$${fmt(Math.round(hCalc.heliCharterCost))} (${hCalc.heliFlights}× ${toHHMM(hCalc.heliRTHrs)} × $${fmt(heliEffRate)}/hr)`} />
                                    
                                    <SpecRow label="Daily total" value={`$${fmt(Math.round(hCalc.heliDailyTotal))}`} variant={isThisCheapest ? "good" : "warn"} bold />
                                    <SpecRow label="Cost/pax" value={`$${fmtD(hCalc.heliCostPerPax)}`} variant={isThisCheapest ? "good" : "warn"} bold />
                                    <SpecRow label="CO₂/pax" value={`${fmt(Math.round(hCalc.heliCO2PerPax))} kg`} variant={isThisCheapest ? "good" : "warn"} />
                                  </div>
                                </div>
                              </FadeIn>
                            );
                          })}
                          {/* AIRCAT 35 card */}
                          <FadeIn delay={0.2 + fi * 0.05}>
                            <div className={`bg-card rounded-xl p-5 relative overflow-hidden ${isAircatCheapest ? 'border-2 border-teal' : 'border border-border'}`}>
                              {isAircatCheapest && <div className="absolute top-0 right-0 bg-teal text-white text-[9px] font-bold tracking-widest uppercase px-3 py-1 rounded-bl-lg">✓ RECOMMENDED</div>}
                              <span className="inline-block text-[11px] px-3 py-1 rounded-md bg-teal/10 text-teal font-semibold mb-3">CTV — Velocity Offshore</span>
                              <div className="font-display text-sm font-bold text-foreground mb-0.5">AIRCAT 35 Crewliner</div>
                              <div className="text-[10px] text-muted-foreground mb-3">{AIRCAT_PAX} pax · {AIRCAT_SPEED_KTS} kts · {AIRCAT_MGO_BURN_MT_HR} MT/hr MGO</div>
                              <div className="space-y-0">
                                <SpecRow label="Voyages" value={`${fR.aircatTrips} (${fieldPax} ÷ ${AIRCAT_PAX})`} variant={isAircatCheapest ? "good" : undefined} />
                                <SpecRow label="RT time" value={toHHMM(fR.aircatRTHrs)} />
                                <SpecRow label="Total time" value={toHHMM(fR.aircatRTHrs * fR.aircatTrips)} />
                                <SpecRow label="Fuel" value={`${fmtD(fR.aircatFuelMTPerTrip * fR.aircatTrips, 2)} MT MGO · $${fmt(Math.round(fR.aircatTotalFuelCost))}`} variant={isAircatCheapest ? "good" : undefined} />
                                
                                <SpecRow label="Day rate" value={`$${fmt(fr.aircatDayRate)}/day`} />
                                <SpecRow label="Daily total" value={`$${fmt(Math.round(fR.aircatDailyTotal))}`} variant={isAircatCheapest ? "good" : "warn"} bold />
                                <SpecRow label="Cost/pax" value={`$${fmtD(fR.aircatCostPerPax)}`} variant={isAircatCheapest ? "good" : "warn"} bold />
                                <SpecRow label="CO₂/pax" value={`${fmt(Math.round(fR.aircatCO2PerPax))} kg`} variant="good" />
                              </div>
                            </div>
                          </FadeIn>
                          {/* Competitor vessel cards */}
                          {vesselKeys.map((vk, vi) => {
                            const cv = calcCompetitorVessel(vk, fr.distance, fieldPax, mgoPriceMT, vesselDayRateOverrides[vk], vesselSpeedOverrides[vk]);
                            const sv = COMPETITOR_VESSELS[vk];
                            const effectiveRate = vesselDayRateOverrides[vk] ?? sv.dayRate;
                            const vcol = vesselColors[vi % vesselColors.length];
                            const isThisVesselCheapest = cheapestVesselKey === vk;
                            return (
                              <FadeIn key={vk} delay={0.25 + fi * 0.05 + vi * 0.03}>
                                <div className={`bg-card rounded-xl p-5 relative overflow-hidden ${isThisVesselCheapest ? 'border-2 border-teal' : 'border border-border'}`} style={!isThisVesselCheapest ? { borderColor: `${vcol}33` } : undefined}>
                                  {isThisVesselCheapest && <div className="absolute top-0 right-0 bg-teal text-white text-[9px] font-bold tracking-widest uppercase px-3 py-1 rounded-bl-lg">✓ RECOMMENDED</div>}
                                  <span className="inline-block text-[11px] px-3 py-1 rounded-md font-semibold mb-3" style={{ background: `${vcol}12`, color: vcol }}>{sv.hullType}</span>
                                  <div className="font-display text-sm font-bold text-foreground mb-0.5">{sv.name}</div>
                                  <div className="text-[10px] text-muted-foreground mb-3">{sv.pax} pax · {sv.kts} kts · ${fmt(effectiveRate)}/day · {sv.dp}</div>
                                  <div className="space-y-0">
                                    <SpecRow label="Voyages" value={`${cv.trips} (${fieldPax} ÷ ${sv.pax})`} />
                                    <SpecRow label="RT time" value={toHHMM(cv.rtHrs)} />
                                    <SpecRow label="Total time" value={toHHMM(cv.rtHrs * cv.trips)} />
                                    <SpecRow label="Fuel" value={`${fmtD(cv.fuelMTPerTrip * cv.trips, 2)} MT · $${fmt(Math.round(cv.totalFuelCost))}`} />
                                    
                                    <SpecRow label="Day rate" value={`$${fmt(effectiveRate)}/day${vesselDayRateOverrides[vk] !== undefined ? ' (custom)' : ''}`} />
                                    <SpecRow label="Daily total" value={`$${fmt(Math.round(cv.dailyTotal))}`} variant={isThisVesselCheapest ? "good" : undefined} bold />
                                    <SpecRow label="Cost/pax" value={`$${fmtD(cv.costPerPax)}`} variant={isThisVesselCheapest ? "good" : undefined} bold />
                                    <SpecRow label="CO₂/pax" value={`${fmt(Math.round(cv.co2PerPax))} kg`} />
                                  </div>
                                </div>
                              </FadeIn>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                </div>
              );
            })}

            {/* Comparison charts */}
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                {allHeliResults.map((hr, i) => (
                  <span key={hr.key} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: heliColors[i % heliColors.length] }} /> {hr.heli.name}</span>
                ))}
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: VO_GREEN }} /> AIRCAT 35 (Velocity)</span>
                {selectedVesselResults.map((svr, i) => (
                  <span key={svr.key} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: vesselColors[i % vesselColors.length] }} /> {svr.vessel.name}</span>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <FadeIn delay={0.25}>
                  <div className="bg-card rounded-xl border border-border p-6">
                    <div className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground mb-4">Cost per passenger ($/pax)</div>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={paxCompData} margin={{ bottom: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" interval={0} height={60} />
                        <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${fmt(v)}`} />
                        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v: number) => [`$${fmtD(v)}/pax`, "Cost"]} />
                        <Bar dataKey="cost" radius={[6, 6, 0, 0]} name="Cost/pax">
                          {paxCompData.map((entry, i) => (
                            <Bar key={i} dataKey="cost" fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </FadeIn>
                <FadeIn delay={0.3}>
                  <div className="bg-card rounded-xl border border-border p-6">
                    <div className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground mb-4">Estimated annual cost ($)</div>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={annualCompData} margin={{ bottom: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" interval={0} height={60} />
                        <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${fmt(Math.round(v / 1000))}K`} />
                        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v: number) => [`$${fmt(Math.round(v / 1000))}K/yr`, "Annual cost"]} />
                        <Bar dataKey="cost" radius={[6, 6, 0, 0]} name="Annual cost">
                          {annualCompData.map((entry, i) => (
                            <Bar key={i} dataKey="cost" fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </FadeIn>
              </div>
            </div>

            {/* Generate report button */}
            <div className="text-center py-4">
              <button onClick={() => setReportOpen(true)} className="inline-flex items-center gap-2 px-6 py-3 bg-navy-deep text-off-white rounded-xl text-sm font-medium hover:bg-navy-light transition-colors shadow-lg">
                <FileText className="w-4 h-4" />
                Generate Branded Prospect Report
              </button>
            </div>
          </div>
        )}

        {/* TAB: WEATHER OPERABILITY */}
        {activeTab === "weather" && (
          <WeatherOperability
            heliKeys={heliKeys}
            vesselKeys={vesselKeys}
            helis={HELIS}
            vessels={COMPETITOR_VESSELS}
            blocks={BLOCKS}
            selectedIdxs={selectedIdxs}
            heliRateOverrides={heliRateOverrides}
            vesselDayRateOverrides={vesselDayRateOverrides}
            opsDays={opsDays}
            mgoPriceMT={mgoPriceMT}
            jetFuelPerGal={jetFuelPerGal}
            aircatDayRate={aircatDayRate}
            aircatPax={AIRCAT_PAX}
            aircatSpeedKts={AIRCAT_SPEED_KTS}
            aircatMgoBurnMTHr={AIRCAT_MGO_BURN_MT_HR}
            aircatMaintHr={AIRCAT_MAINT_HR}
            totalPax={totalPax}
            fieldPaxOverrides={fieldPaxOverrides}
          />
        )}

        {/* TAB: REPORT */}
        {activeTab === "report" && (
          <FadeIn>
            <div className="bg-card rounded-xl border border-border p-12 text-center">
              <div className="text-5xl mb-4">📄</div>
              <h3 className="font-display text-xl font-bold text-foreground mb-2">Branded Prospect Report</h3>
              <p className="text-muted-foreground text-sm max-w-lg mx-auto mb-6">Configure your scenario in the Crew Transfer Comparator tab first, then generate a ready-to-share PDF report.</p>
              <button
                onClick={() => { setActiveTab("analysis"); setTimeout(() => setReportOpen(true), 300); }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-navy-deep text-primary-foreground rounded-xl text-sm font-medium hover:bg-navy-light transition-colors"
              >
                <FileText className="w-4 h-4" />
                Go to Comparator & Generate Report
              </button>
            </div>
          </FadeIn>
        )}
      </Section>

      {/* CTA */}
      <Section dark>
        <div className="text-center">
          <SectionLabel>Why CTV?</SectionLabel>
          <SectionTitle light>Lower cost. Fewer flights. <span className="accent-text">Safer.</span></SectionTitle>
          <div className="grid sm:grid-cols-3 gap-8 mt-12 max-w-3xl mx-auto">
            {[
              { icon: TrendingDown, label: "Lower Operating Cost", desc: "MGO + lower day rates deliver significantly lower total cost per passenger than helicopter" },
              { icon: Shield, label: "Reduced Risk Exposure", desc: `1 voyage replaces ${Math.ceil(28 / 12)} helicopter flights for a 28-pax crew change` },
              { icon: Leaf, label: "Lower Emissions", desc: "Substantially reduced CO₂ per passenger transferred compared to helicopter" },
            ].map((item) => (
              <FadeIn key={item.label}>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-secondary/10 mb-4">
                    <item.icon className="w-6 h-6 text-secondary" />
                  </div>
                  <h4 className="font-display text-primary-foreground font-semibold mb-2">{item.label}</h4>
                  <p className="text-sm text-primary-foreground/80">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </Section>

      <ReportModal
        open={reportOpen} onClose={() => setReportOpen(false)}
        heliKey={primaryHeliKey} agg={agg}
        jetFuelPerGal={jetFuelPerGal} mgoPriceMT={mgoPriceMT}
        allHeliResults={allHeliResults}
        heliRateOverrides={heliRateOverrides}
        selectedVesselResults={selectedVesselResults}
        vesselDayRateOverrides={vesselDayRateOverrides}
        opsDays={opsDays}
        fieldBlocks={selectedIdxs.map(idx => ({ name: BLOCKS[idx].name, fieldName: BLOCKS[idx].fieldName, nm: BLOCKS[idx].nm }))}
        fieldPaxList={selectedIdxs.map(idx => fieldPaxOverrides[idx] ?? totalPax)}
      />
    </>
  );

  if (skipLayout) {
    return ctiContent;
  }

  return (
    <Layout>
      <PageHero
        tag="Crew Transfer Intelligence"
        title={<>Fuel cost analysis, <span className="accent-text">live.</span></>}
        subtitle="Compare helicopter vs CTV crew transfer costs with live fuel pricing, configurable scenarios, and branded prospect reports — powered by Velocity Offshore."
        backgroundImage={operationsBg}
      />
      {ctiContent}
    </Layout>
  );
};

export default CrewTransferIntelligence;
